import { CurrencyControl } from '../components/CurrencyControl'
import { gridsOnTab } from '../api/viewScope'
import { restore, snapshot } from '../components/gridLayout'
import { restoreSort, snapshotSort } from '../components/gridSort'
import {
  NOTHING_DROPPED,
  SavedViewsMenu,
  VIEW_SCHEMA,
  ViewAdjustedNotice,
  parseView,
  useSavedViews,
  useViewDirty,
  type DropReport,
  type ViewPayload,
} from '../components/SavedViews'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PARTIAL_NOTE, PARTIAL_TITLE, WITHHELD, WITHHELD_TITLE, partialOf, sumShown } from '../api/answer'
import type { Api } from '../api'
import { ApiError, LOCAL, SALES_DEFAULT_DRILL, USD, inCurrency, isConverted, resolveSalesDrill, toParam } from '../api/types'
import type {
  ApiFailure,
  Currency,
  SalesBoardRow,
  SalesDimension,
  SalesDrillChoice,
  SalesDrillLevel,
  SalesExplorerDimension,
  SalesExplorerResponse,
  SalesExplorerRow,
  SalesFilterRow,
  SalesFilters,
  SalesOverview,
  SalesPeriod,
  SalesTreeResponse,
  SalesTrendRow,
  CurrencyRate,
  StoredReaderView,
} from '../api/types'
import { Button, Card, CardHeader, CollapsibleStats, EmptyState, Loading, MultiSearchSelect, NAME_PANEL_WIDTH, PageTabs, ReportHeader, SearchSelect, Segmented, StatTile, compact, inputClass, money, useSvgTextScale, currencyShort, currencyNote } from '../components/ui'
import { FailureState } from '../components/FailureState'
import { ExpandableCard } from '../components/ExpandableCard'
import { DevModeProvider, salesRequestSummary } from '../components/MeasurePanel'
import { Explain, ExplainMark, useExplain } from '../components/DevModeContext'
import { ExportButtons } from '../components/ExportButtons'
import { useReportCurrency } from '../components/rememberedCurrency'
import { quantityUnits } from '../components/units'
import { GridChrome, ResizeHandle, useGridLayout } from '../components/gridLayout'
import { SortHeader, compareBy, useGridSort, useSortMirror, type SortState } from '../components/gridSort'
import type { ReactNode } from 'react'
import { SalesInvoiceLines } from '../components/SalesInvoiceLines'
import { chartFromSvg, type ExportDoc, type ExportRow } from '../export/model'

/**
 * SALES — the Power BI "Sales Dashboard" and "Sales DashboardV2", merged and
 * rebuilt. The study that decided the merge is artifacts/sales-import/PLAN.md:
 * their 21 pages were overwhelmingly one screen repeated with a different
 * grouping, so this page is four surfaces instead —
 *
 *   EXPLORER      one grid, nine groupings, YTD or MTD   (replaces 16 pages)
 *   LEADERBOARDS  top five by sales and by GP            (replaces "By BU Backup")
 *   TREND         monthly sales and GP, this year v last (rescued from V1)
 *   TREE          decomposition by any dimension         (replaces the tree pages)
 *
 * — with the four slicers every Power BI page repeated promoted to ONE standing
 * filter bar that every tab obeys.
 *
 * ---------------------------------------------------------------------------
 * EVERY REPORT KEEPS ITS OWN FILE. This one is sales'. Receivables lives in
 * ReceivablesAgingPage.tsx and its components; nothing here imports from
 * there, and editing one report cannot touch the other. Shared primitives
 * (Card, Segmented, StatTile, the failure panel) come from components/ui.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE READER MUST BE ABLE TO TRUST
 *
 * A masked figure is NULL and renders as an em dash — never zero. The database
 * masks before it sums (Sales_Cost gates every margin figure), so "not shown
 * to you" and "nothing there" stay different answers. GP leaderboards are
 * ABSENT for a caller without Sales_Cost, not blank: names ordered by margin
 * would still disclose the margin order.
 *
 * "Business Unit" here is the Power BI report's vocabulary and it is kept —
 * but it is Customer_Class underneath, a customer taxonomy, and it plays no
 * part in row security. Row security is the platform's grant table, read by
 * rpt.fn_Caller_Sales_Scope (015): company and team, a one-unit grant read as
 * the teams of that unit (a team placed in the unit its customers sit in)
 * since 7 Oct 2026. Totals for scoped users may
 * read LOWER than the old Power BI report. Deliberate.
 */

type TabId = 'explorer' | 'leaderboards' | 'trend' | 'tree' | 'lines'

const TABS: { id: TabId; label: string }[] = [
  { id: 'explorer', label: 'Explorer' },
  { id: 'leaderboards', label: 'Leaderboards' },
  { id: 'trend', label: 'Monthly Trend' },
  { id: 'tree', label: 'Tree View' },
  /* Requested by management 9 Aug 2026 — the posted ledger, one row per line.
     Last because it is the detail the other four summarise. */
  { id: 'lines', label: 'Invoice Lines' },
]

/**
 * The dimensions the TREE offers. Company is deliberately absent: a tree path
 * step becomes a standing filter parameter, and the tree's company filter
 * takes the DATAAREAID code while a grouping yields the company name — the
 * step would filter to nothing. The explorer's drill has no such seam
 * (it compares against the grouping expression itself), so it offers Company
 * through EXPLORER_DIMENSIONS below.
 *
 * The two countries are explorer-only for the same reason, and more firmly:
 * there is no country filter for a tree step to become at all. They live in
 * SalesExplorerDimension, so the tree's types cannot even name them.
 */
const DIMENSIONS: { value: SalesDimension; label: string }[] = [
  { value: 'BusinessUnit', label: 'Business Unit' },
  { value: 'BusinessTeam', label: 'Business Team' },
  { value: 'Salesperson', label: 'Salesperson' },
  { value: 'Customer', label: 'Customer' },
  { value: 'Product', label: 'Product' },
  { value: 'Supplier', label: 'Supplier' },
]

/**
 * Ordered from the top of the organisation down, each grouping beside the one
 * it drills into. Country leads -- the selling company's country is the only
 * grouping above Company -- and Customer Country sits immediately before
 * Customer, which is where it drills.
 */
const EXPLORER_DIMENSIONS: { value: SalesExplorerDimension; label: string }[] = [
  { value: 'Country', label: 'Country' },
  { value: 'Company', label: 'Company' },
  { value: 'BusinessUnit', label: 'Business Unit' },
  { value: 'BusinessTeam', label: 'Business Team' },
  { value: 'Salesperson', label: 'Salesperson' },
  { value: 'CustomerCountry', label: 'Customer Country' },
  { value: 'Customer', label: 'Customer' },
  { value: 'Product', label: 'Product' },
  { value: 'Supplier', label: 'Supplier' },
]

/**
 * What a drill level may be, and what it is called. Levels 2 and 3 are the
 * reader's choice since 30 Sep 2026, level 4 since 1 Oct 2026 (the sales
 * manager's request): every grouping above, plus Item # -- the item number,
 * which is a level but never a grouping of its own. The DEFAULT chain per
 * grouping lives in SALES_DEFAULT_DRILL (types.ts), beside the rule that
 * resolves a request, because the demo client needs both too.
 *
 * The two countries are names ("Saudi Arabia", never "SAU"); the customer's
 * falls back to the customer region where D365 left the country blank.
 * 038_country_reference.sql holds the list and the rules.
 */
const DRILL_OPTIONS: { value: SalesDrillLevel; label: string }[] = [
  ...EXPLORER_DIMENSIONS,
  { value: 'Item', label: 'Item #' },
]
const DRILL_LABEL = Object.fromEntries(DRILL_OPTIONS.map((o) => [o.value, o.label])) as Record<SalesDrillLevel, string>
const sameDrill = (a: readonly SalesDrillLevel[], b: readonly SalesDrillLevel[]) =>
  a.length === b.length && a.every((l, i) => l === b[i])

/**
 * A drill order as a SAVED VIEW stores it: level 4 only when the reader set it
 * apart from what levels 2 and 3 resolve to by themselves. A view saved while
 * the chain was three levels deep holds just the pair, and applying it gives
 * the automatic fourth level -- so writing the fourth back unconditionally
 * would mark every such view "Edited" the moment it opened.
 */
const viewDrill = (group: SalesExplorerDimension, d: SalesDrillChoice): SalesDrillLevel[] =>
  d[2] === resolveSalesDrill(group, d[0], d[1])[3] ? [d[0], d[1]] : [...d]

export function SalesPage({
  api,
  viewAs = null,
  roles = [],
}: {
  api: Api
  viewAs?: string | null
  /**
   * Only to decide whether a column header LOOKS clickable. Developer mode is
   * enforced by the API, which answers 404 to anyone who is not a System Admin
   * and to everyone while the switch is off — editing this in a debugger
   * produces an empty panel, not a disclosure.
   */
  roles?: string[]
}) {
  const [tab, setTab] = useState<TabId>('explorer')
  /* Period and currency are REPORT-level, exactly like the receivables
     currency control: two tabs showing two periods at once would put two
     answers to "how are we doing" on one screen with nothing saying which. */
  const [period, setPeriod] = useState<SalesPeriod>('ytd')
  /* USD BY DEFAULT — the owner's call, 23 Sep 2026, for every report.
     ------------------------------------------------------------------------
     The fact holds 22 functional currencies, one per company, so a reader
     scoped across companies opening in Local is adding riyals to dirhams and
     no single label on the screen is true of the whole figure. Converted, the
     total means something the moment it appears. Local is still one click
     away, and it is the honest choice for a single-company reader checking a
     figure against the ERP.

     Nothing else had to move: the client expresses Local by OMITTING the
     currency parameter, so the API's and the procedures' own `local` defaults
     stay correct as the "caller said nothing" answer. */
  /* REMEMBERED since 4 Oct 2026: the report opens in the currency this reader
     last chose on ANY report, and a click on the toggle is remembered for the
     next one -- see components/rememberedCurrency.ts. USD stays the default for
     a reader who has never chosen. `setCurrency` -- a saved view applying its
     own currency -- is deliberately NOT remembered. */
  const [currency, chooseCurrency, setCurrency] = useReportCurrency(api, viewAs)
  const [filters, setFilters] = useState<SalesFilters>({})
  /* THE EXPLORER'S EXTRACT, published up to the card header -- the Receivables
     matrix pattern. The builder stays with the grid's state; only the buttons
     move. They moved on 29 Sep 2026 because nine Group-by pills no longer fit
     one line beside them on a laptop: measured at 1536x864, the band wrapped
     and the grid lost 1.4 rows (10.8 -> 9.4). In the header they sit beside
     Columns and Full screen, where the other two reports already keep them. */
  const explorerExport = useRef<(() => ExportDoc | null) | null>(null)
  /* THE EXPLORER'S GROUPING AND DRILL ORDER live HERE, not inside the grid
     (30 Sep 2026): a saved view is captured at page level, and both are part of
     one. `explorerDrill` is levels 2 to 4 when the reader has chosen them and
     null for the grouping's own default -- which is also what choosing another
     grouping resets it to (owner's choice), so every grouping still opens on
     the chain its readers know. */
  const [explorerDim, setExplorerDim] = useState<SalesExplorerDimension>('BusinessUnit')
  const [explorerDrill, setExplorerDrill] = useState<SalesDrillChoice | null>(null)
  const chooseExplorerDim = useCallback((d: SalesExplorerDimension) => {
    setExplorerDim(d)
    setExplorerDrill(null)
  }, [])

  /* THE CUSTOM RANGE — year, narrowed by month, narrowed by week. The pickers
     cascade: a year alone is the whole year, a month narrows to it, a week
     (Sun–Sat, the platform convention) narrows further. The chosen span is
     compared against the same span one year earlier, like YTD and MTD are. */
  /* NULL until the scoped option lists land, and that distinction is
     load-bearing: a saved view decides what to DROP by asking what is offered,
     so applying one before the menus arrive would retract filters in front of
     the reader. An empty array means the read finished and found nothing. */
  const [filterOptions, setFilterOptions] = useState<SalesFilterRow[] | null>(null)
  const [customYear, setCustomYear] = useState('')
  const [customMonth, setCustomMonth] = useState('')
  const [customWeek, setCustomWeek] = useState('')

  const years = useMemo(() => {
    const y = (filterOptions ?? []).filter((o) => o.kind === 'year').map((o) => o.code ?? '')
    return [...new Set(y.filter(Boolean))].sort((a, b) => b.localeCompare(a))
  }, [filterOptions])

  /* Code → name, so an extract can say "REDAChem KSA" where the wire says
     "r002". Read from the same scoped options the filter bar offers. */
  const companyNames = useMemo(
    () =>
      new Map(
        (filterOptions ?? [])
          .filter((o) => o.kind === 'company' && o.code)
          .map((o) => [o.code as string, o.label || (o.code as string)]),
      ),
    [filterOptions],
  )

  // First entry into custom mode: preselect the newest year with data.
  useEffect(() => {
    if (period === 'custom' && !customYear && years.length) setCustomYear(years[0])
  }, [period, years, customYear])

  const range = useMemo(
    () => (period === 'custom' && customYear ? customRange(customYear, customMonth, customWeek) : null),
    [period, customYear, customMonth, customWeek],
  )

  /* ---------------------------------------------------------------------
     SAVED VIEWS.

     Same adapter as Receivables', and deliberately the same SHAPE rather than
     a shared function: capture() and apply() read this page's own state, and a
     shared version would have to be told about every picker either report
     gains. What IS shared is the payload contract and the menu.

     Sales captures two things Receivables has no equivalent of -- the period
     and the custom range -- and the period is stored as its MODE. A view that
     pinned "1 Jan to 24 Sep 2026" would answer a different question next year
     while still calling itself "My YTD", so only the reader's own chosen
     custom range is absolute, and only while custom is the mode.
     --------------------------------------------------------------------- */
  const { views, save, remove, rename, setDefault } = useSavedViews(api, 'sales', viewAs)
  const [activeView, setActiveView] = useState<number | null>(null)
  const [adjusted, setAdjusted] = useState<DropReport>(NOTHING_DROPPED)
  /** Which tab's default has already been applied, so it happens once. */
  const appliedDefault = useRef<Set<string>>(new Set())

  /* The live rate table, for a view saved in a named currency. The view stores
     the CODE; the rate is always looked up now. See the currency note in
     apply() -- Receivables' copy carries the full reasoning. */
  const [rates, setRates] = useState<CurrencyRate[] | null>(null)
  useEffect(() => {
    let cancelled = false
    api.currencyRates().then((r) => !cancelled && setRates(r)).catch(() => !cancelled && setRates([]))
    return () => {
      cancelled = true
    }
  }, [api])

  const tabViews = useMemo(() => views.filter((v) => v.pageSlug === tab), [views, tab])

  const capture = useCallback((): ViewPayload => {
    const grids: Record<string, { order?: string[]; hidden?: string[]; sort?: SortState }> = {}
    for (const gridId of gridsOnTab('sales', tab)) {
      const snap = snapshot(gridId)
      const sorted = snapshotSort(gridId)
      /* ORDER, HIDDEN AND THE SORT -- never widths or pinning. Those are
         physical and screen-dependent, and a view that also resized columns
         would fight the reader's own drag every time they switched views. The
         silent layer already keeps them, per person per grid. */
      if (!snap && !sorted?.key) continue
      grids[gridId] = {
        ...(snap ? { order: snap.order, hidden: snap.hidden } : {}),
        ...(sorted?.key ? { sort: sorted } : {}),
      }
    }
    return {
      v: VIEW_SCHEMA,
      currency: toParam(currency) ?? 'local',
      period,
      /* Only meaningful in custom mode, and only written there: a stale year
         sitting in every YTD view is a trap waiting for somebody to switch. */
      custom: period === 'custom' ? { year: customYear, month: customMonth, week: customWeek } : null,
      filters,
      grids,
      /* The Explorer's grouping and drill order, written only when they differ
         from what the tab opens on. A view saved before 30 Sep 2026 carries
         neither, and must not read as changed the moment it is applied --
         an undefined key is dropped from the comparison (see stableJson). */
      explorer:
        tab === 'explorer' && (explorerDim !== 'BusinessUnit' || explorerDrill)
          ? { groupBy: explorerDim, ...(explorerDrill ? { drill: viewDrill(explorerDim, explorerDrill) } : {}) }
          : undefined,
    }
  }, [tab, currency, period, customYear, customMonth, customWeek, filters, explorerDim, explorerDrill])

  const apply = useCallback(
    (payload: ViewPayload): DropReport => {
      const dropped: string[] = []
      const offered = (kind: SalesFilterRow['kind'], code: string) =>
        !filterOptions || filterOptions.some((o) => o.kind === kind && o.code === code)

      /* FILTERS: a value the reader can no longer be offered is left out rather
         than applied. It could not disclose anything — a filter only narrows,
         and the procedure re-checks scope regardless — but a control naming a
         supplier the book does not contain is the screen lying about itself. */
      const stored = (payload.filters ?? {}) as SalesFilters
      const next: SalesFilters = {}

      /* A LIST, WHATEVER IS ACTUALLY IN THERE. The payload came out of a
         database and could have been written by a build that meant something
         different by the same field name -- which is not hypothetical: `sup`
         held a single STRING until 25 Sep 2026, and a string has a truthy
         `.length` and no `.filter`, so reading one as a list would throw
         inside an effect and take the whole report into the error boundary.
         A lone string MIGRATES to a one-member list rather than being dropped,
         because that is what the reader chose and it still means the same
         thing. Anything else is discarded, as parseView discards a payload it
         does not recognise. */
      const asList = (v: unknown): string[] =>
        Array.isArray(v)
          ? v.filter((x): x is string => typeof x === 'string' && x !== '')
          : typeof v === 'string' && v !== ''
            ? [v]
            : []

      const companies = asList(stored.company)
      if (companies.length) {
        const live = companies.filter((c) => offered('company', c))
        if (live.length) next.company = live
        if (live.length !== companies.length) dropped.push('some companies')
      }
      const suppliers = asList(stored.sup)
      if (suppliers.length) {
        const live = suppliers.filter((c) => offered('supplier', c))
        if (live.length) next.sup = live
        if (live.length !== suppliers.length) dropped.push('some suppliers')
      }
      const keep = (
        k: 'bu' | 'bt' | 'sp' | 'reg' | 'cust',
        kind: SalesFilterRow['kind'] | null,
        label: string,
      ) => {
        const v = stored[k]
        if (typeof v !== 'string' || v === '') return
        if (kind && !offered(kind, v)) {
          dropped.push(label)
          return
        }
        next[k] = v
      }
      keep('bu', 'businessUnit', 'the business unit')
      keep('bt', 'businessTeam', 'the business team')
      keep('sp', 'salesperson', 'the salesperson')
      keep('reg', 'region', 'the customer region')
      // Customer is matched as a substring and has no list to go stale against.
      keep('cust', null, 'the customer')
      setFilters(next)

      /* THE PERIOD IS A MODE. The custom range rides along only when the mode
         is custom, and only while the year is still one the caller's scope
         contains — a view naming 2024 for somebody whose data now starts in
         2025 falls back to YTD rather than showing an empty year. */
      const mode: SalesPeriod =
        payload.period === 'mtd' || payload.period === 'custom' ? payload.period : 'ytd'
      const custom = (payload.custom ?? {}) as { year?: string; month?: string; week?: string }
      if (mode === 'custom' && (!custom.year || (years.length > 0 && !years.includes(custom.year)))) {
        dropped.push('the saved date range')
        setPeriod('ytd')
      } else {
        setPeriod(mode)
        if (mode === 'custom') {
          setCustomYear(custom.year ?? '')
          setCustomMonth(custom.month ?? '')
          setCustomWeek(custom.week ?? '')
        }
      }

      /* CURRENCY: the CODE is stored, never the rate. The client's rate is
         display-only — the server converts by code — so a stored rate would
         print last quarter's number over figures converted at this one. */
      const code = typeof payload.currency === 'string' ? payload.currency : 'local'
      if (code === 'local') setCurrency(LOCAL)
      else if (code === 'usd') setCurrency(USD)
      else if (rates) {
        const live = rates.find((r) => r.code.toLowerCase() === code.toLowerCase())
        if (live) setCurrency(inCurrency(live.code, live.ratePerUsd))
        else dropped.push(`the ${code.toUpperCase()} currency`)
      }

      // GRIDS: filtered to the columns this reader actually has.
      const grids = (payload.grids ?? {}) as Record<string, { order?: string[]; hidden?: string[]; sort?: { key?: unknown; desc?: unknown } }>
      for (const [gridId, g] of Object.entries(grids)) {
        const current = snapshot(gridId)
        if (!current || !Array.isArray(g.order)) continue
        restore(gridId, {
          ...current,
          order: g.order.filter((k) => current.order.includes(k)),
          hidden: (g.hidden ?? []).filter((k) => current.order.includes(k)),
        })
      }

      /* THE SORT, for every grid on this tab and not only the ones the view
         mentions: a view is the whole arrangement, so one saved with no sort
         puts the grid back to its natural order rather than leaving the last
         question the reader asked sitting over somebody else's answer. Each
         grid checks the key against its own columns -- see useSortMirror. */
      for (const gridId of gridsOnTab('sales', tab)) {
        const s = grids[gridId]?.sort
        restoreSort(
          gridId,
          s && typeof s.key === 'string' && s.key ? { key: s.key, desc: s.desc !== false } : { key: null, desc: true },
        )
      }

      /* THE EXPLORER'S GROUPING AND DRILL ORDER. Absent means the tab's own
         opening state, not "leave it as it is": a view is the whole screen, the
         same rule as the sort above. A grouping this build no longer offers
         drops the pair; a drill order is kept only as distinct levels that all
         still exist and none repeats the grouping -- anything else falls back
         to the grouping's default, and the reader is told.

         TWO LEVELS OR THREE. A view saved before 1 Oct 2026 holds levels 2 and
         3 only; it gets the fourth the rule picks for them, exactly what the
         Explorer would show had the reader set those two by hand today. */
      if (tab === 'explorer') {
        const ex = (payload.explorer ?? {}) as { groupBy?: unknown; drill?: unknown }
        const dim = EXPLORER_DIMENSIONS.find((d) => d.value === ex.groupBy)?.value
        if (ex.groupBy !== undefined && !dim) dropped.push('the saved grouping')
        const group = dim ?? 'BusinessUnit'
        setExplorerDim(group)
        const d = ex.drill
        const valid =
          Array.isArray(d) &&
          (d.length === 2 || d.length === 3) &&
          d.every((l) => DRILL_OPTIONS.some((o) => o.value === l)) &&
          new Set([group, ...d]).size === d.length + 1
        if (d !== undefined && (!dim || !valid)) dropped.push('the saved drill order')
        if (dim && valid) {
          const [, c2, c3, c4] = resolveSalesDrill(group, d[0] as string, d[1] as string, d[2] as string | undefined)
          const choice: SalesDrillChoice = [c2, c3, c4]
          setExplorerDrill(sameDrill(choice, SALES_DEFAULT_DRILL[group]) ? null : choice)
        } else setExplorerDrill(null)
      }

      return { dropped }
    },
    [filterOptions, rates, years, tab],
  )

  /* See ReceivablesAgingPage: the chip is a caption beside the title. */
  const viewDirty = useViewDirty(views, activeView, capture)

  const applyStored = useCallback(
    (v: StoredReaderView) => {
      const payload = parseView(v.view, v.schemaVersion)
      if (!payload) return
      setAdjusted(apply(payload))
      setActiveView(v.viewId)
    },
    [apply],
  )

  /* THE DEFAULT, ONCE PER TAB, AND ONLY AFTER THE MENUS HAVE LANDED. Applying
     before the option lists arrive would mean deciding what to drop without
     knowing what is offered. */
  useEffect(() => {
    if (!filterOptions || !rates) return
    if (appliedDefault.current.has(tab)) return
    const def = views.find((v) => v.pageSlug === tab && v.isDefault)
    appliedDefault.current.add(tab)
    if (def) applyStored(def)
  }, [filterOptions, rates, views, tab, applyStored])

  // A different pair of eyes means different views and a different book.
  useEffect(() => {
    appliedDefault.current = new Set()
    setActiveView(null)
    setAdjusted(NOTHING_DROPPED)
  }, [viewAs])

  const active = TABS.find((t) => t.id === tab) ?? TABS[0]

  /* THE FRESHNESS WATERMARK WAS REMOVED ON 15 Sep 2026. Owner's decision, and
     the reason is worth keeping so nobody restores it as an improvement.

     It showed MAX(Invoice_Date) and was labelled "Posted sales through <date>".
     Its stated purpose (020_sales_columns.sql:67) was that "a stalled upstream
     job is visible instead of silent". Production breaks both halves of that:
     measured 15 Sep 2026, RCBYOD's newest invoice is dated 27 Sep 2026 -- twelve
     days in the FUTURE. So the header would have read "Posted sales through
     27 September 2026" on the fifteenth, and the stall detector is blind for as
     long as the forward-dating window, because the maximum stays ahead of today
     whether or not the job ever runs again.

     The clone has ZERO future-dated rows, which is why none of this was visible
     in any parity check, fixture or measurement: the two things FreshThrough
     conflates -- when the data last ARRIVED, and how far the invoices REACH --
     had the same answer there, and only there.

     If it comes back it needs the load time (MNDSalesOrderInvoiceLinesStaging
     .SYNCSTARTDATETIME), not a business date standing in for one. The view
     exposes no load timestamp at all, which is the deeper reason this never
     worked: the signal was never available at this layer.

     rpt.usp_Sales_Freshness and the /sales/freshness endpoint stayed in place,
     uncalled, until 27 Oct 2026, when the owner had them removed (D50). Bringing
     the date back now means a read of the load time, as above, not that one. */

  /* The KPI head's data, lifted so the Explorer's extract can carry the tiles
     the reader sees above the grid — an export is the screen, re-typeset. */
  const [overview, setOverview] = useState<SalesOverview | null>(null)

  /* The card subtitles name the toggle's position, not a specific code: the
     code belongs to a ROW, and the rows carry their own (see dimsOfRows). */
  const currencyPhrase = currencyShort(currency)

  return (
    /* DEVELOPER MODE, ONCE, FOR THE WHOLE REPORT.
       Everything below can now offer an explanation for any documented figure,
       column or control without being handed a single extra prop. The provider
       asks the server which keys are documented and renders the panel itself,
       so a header buried six components deep neither owns a slide-over nor has
       to be told whether the switch is on.

       `request` is the standing selection as it is RIGHT NOW -- recomputed on
       every render on purpose, because the panel should describe the screen the
       reader is looking at, not the screen as it was when they clicked. */
    <DevModeProvider
      api={api}
      roles={roles}
      report="sales"
      request={salesRequestSummary(filters, {
        Period: period === 'custom' ? (range?.label ?? 'custom range') : period.toUpperCase(),
        Currency: currencyShort(currency),
      })}
    >
    {/* gap-4, not gap-5: four stacked chrome blocks sit above the card, so
        every step here costs three times over and the leaderboards' eight
        boards are sized to the pixel to reach the bottom of the window. */}
    <div className="stack-tight flex h-full min-h-0 flex-col gap-4">
      <ReportHeader
        report="Sales"
        screen={active.label}
        what="Posted sales."
        viewAs={viewAs}
        view={
          <SavedViewsMenu
            views={tabViews}
            activeId={activeView}
            acting={viewAs}
            dirty={viewDirty}
            onApply={applyStored}
            onRename={rename}
            onSave={async (name, makeDefault) => {
              setActiveView(await save(tab, name, capture(), makeDefault))
            }}
            onDelete={(id) => {
              void remove(id)
              if (id === activeView) setActiveView(null)
            }}
            onSetDefault={(id, on) => void setDefault(id, on)}
          />
        }
        right={
          <>
            {/* YTD/MTD everywhere a period exists to choose. The one holdout is
                the trend: it IS the months, so the toggle would change nothing
                there — and a control that changes nothing invites belief that
                it did. */}
            {tab !== 'trend' && (
              <Segmented
                surface="filters"
                measureKey="period"
                label="Period"
                value={period}
                onChange={setPeriod}
                options={[
                  { value: 'ytd', label: 'YTD', title: 'Year to date against the same span last year' },
                  { value: 'mtd', label: 'MTD', title: 'Month to date against the same month last year' },
                  { value: 'custom', label: 'Custom', title: 'A year, a month, or a week of your choosing' },
                ]}
              />
            )}
            <CurrencyControl api={api} surface="filters" value={currency} onChange={chooseCurrency} />
          </>
        }
      />

      {/* THE CUSTOM RANGE PICKERS — year alone shows the year, adding a month
          narrows to it, adding a week narrows to that week. Weeks are Sun–Sat
          and labelled by their real dates, so nobody has to know week math. */}
      {tab !== 'trend' && period === 'custom' && (
        <div className="no-print flex shrink-0 flex-wrap items-end gap-3">
          <div className="flex min-w-[120px] flex-col gap-1 max-w-[160px]">
            <Explain surface="filters" measureKey="year" className="eyebrow">
              Year
            </Explain>
            <SearchSelect
              label="Year"
              value={customYear}
              onChange={(v) => {
                setCustomYear(v)
                setCustomMonth('')
                setCustomWeek('')
              }}
              options={years.map((y) => ({ value: y, label: y }))}
            />
          </div>
          <div className="flex min-w-[140px] flex-col gap-1 max-w-[180px]">
            <span className="eyebrow">Month</span>
            <SearchSelect
              label="Month"
              value={customMonth}
              onChange={(v) => {
                setCustomMonth(v)
                setCustomWeek('')
              }}
              options={[
                { value: '', label: 'Whole year' },
                ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m })),
              ]}
            />
          </div>
          {customMonth && (
            <div className="flex min-w-[180px] flex-col gap-1 max-w-[230px]">
              <span className="eyebrow">Week</span>
              <SearchSelect
                label="Week"
                value={customWeek}
                onChange={setCustomWeek}
                options={[
                  { value: '', label: 'Whole month' },
                  ...weeksOf(Number(customYear), Number(customMonth)).map((w) => ({
                    value: w.value,
                    label: w.label,
                  })),
                ]}
              />
            </div>
          )}
          {range && (
            <p className="pb-2 text-(length:--text-small) text-(--color-ink-3)">
              Showing <span className="font-medium text-(--color-ink-2)">{range.label}</span> against
              the same span one year earlier.
            </p>
          )}
        </div>
      )}

      <PageTabs tabs={TABS} value={tab} onChange={setTab} label="Sales pages" />

      <ViewAdjustedNotice report={adjusted} onDismiss={() => setAdjusted(NOTHING_DROPPED)} />

      {/* THE STANDING FILTER BAR — the four slicers every Power BI page
          repeated, stated once and obeyed by every tab. Options come from the
          server, computed inside the caller's scope: a menu naming a company
          the caller cannot open would teach them the company exists. */}
      <SalesFilterBar api={api} viewAs={viewAs} value={filters} onChange={setFilters} onOptions={setFilterOptions} />

      {tab === 'explorer' && (
        <div className="no-print shrink-0">
          {/* Collapsible so the Explorer grid below can take the whole viewport
              when the reader wants the data, not the headline. Keyed to Sales so
              the choice does not leak into Receivables. */}
          <CollapsibleStats storageKey="stats:sales">
            <SalesKpis
              api={api}
              viewAs={viewAs}
              period={period}
              range={range}
              currency={currency}
              filters={filters}
              onData={setOverview}
            />
          </CollapsibleStats>
        </div>
      )}

      {tab === 'explorer' && (
        /* titleRepeatsTab keeps the short-screen behaviour the comment
           below describes, but now only the WORDS go: the full-screen
           button lives in the same slot and stays, which is the screen
           where a reader most wants it. */
        <ExpandableCard
          titleRepeatsTab
          breadcrumb={['Sales', 'Explorer']}
          title="By country, company, business unit, team, salesperson, customer, product or supplier"
          subtitle={`Choose the grouping, expand a row to drill. ${periodPhrase(period, range)} against the same span last year, in ${currencyPhrase}.`}
          right={<ExportButtons build={() => explorerExport.current?.() ?? null} />}
        >
          <SalesExplorer
            api={api}
            viewAs={viewAs}
            period={period}
            range={range}
            currency={currency}
            filters={filters}
            overview={overview}
            companyNames={companyNames}
            exportRef={explorerExport}
            dimension={explorerDim}
            onDimension={chooseExplorerDim}
            drill={explorerDrill}
            onDrill={setExplorerDrill}
          />
        </ExpandableCard>
      )}

      {tab === 'leaderboards' && (
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardHeader
            title={`Top five, ${period === 'custom' ? (range?.label ?? 'custom range') : period === 'mtd' ? 'month to date' : 'year to date'}`}
            /* The "a rank you cannot see is not drawn" line moved to the
               banner that appears when boards ARE hidden — stated there it
               explains something, stated here it was a second line of
               subtitle on every screen for a case most readers never meet. */
            subtitle={`Customers, products, salespeople and suppliers, ranked by sales and by gross profit, in ${currencyPhrase}. Sample shipments are excluded.`}
          />
          <SalesLeaderboards api={api} viewAs={viewAs} period={period} range={range} currency={currency} filters={filters} companyNames={companyNames} />
        </Card>
      )}

      {tab === 'trend' && (
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardHeader
            title="Monthly sales, this year against last"
            subtitle={`Real monthly amounts from the invoice dates — not year-to-date lines. In ${currencyPhrase}.`}
          />
          <SalesTrend api={api} viewAs={viewAs} currency={currency} filters={filters} companyNames={companyNames} />
        </Card>
      )}

      {tab === 'tree' && (
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardHeader
            title={`Decomposition, ${period === 'custom' ? (range?.label ?? 'custom range') : period === 'mtd' ? 'month to date' : 'year to date'}`}
            /* The second sentence earns its place: the tick boxes are visible,
               but "several at once" is a capability, not a control, and a
               reader who has only ever clicked one bar will not infer it from
               a column of empty boxes. */
            subtitle={`Choose what to analyse and what to break it down by, then click a bar to go deeper, in ${currencyPhrase}. Tick several bars to take them together. The measures offered are the ones you are permitted to see.`}
          />
          <SalesTree api={api} viewAs={viewAs} period={period} range={range} currency={currency} filters={filters} overview={overview} companyNames={companyNames} />
        </Card>
      )}

      {tab === 'lines' && (
        <ExpandableCard
          breadcrumb={['Sales', 'Invoice Lines']}
          title="Posted sales lines"
          subtitle="One row per posted line, in the columns and order of the requested spreadsheet. Sorting and paging happen on the server, so they cover the whole selection rather than this page."
        >
          <SalesInvoiceLines
            api={api}
            viewAs={viewAs}
            period={period}
            range={range}
            currency={currency}
            filters={filters}
            contextLines={salesExportContext(period, range, currency, filters, [], companyNames)}
          />
        </ExpandableCard>
      )}
    </div>
    </DevModeProvider>
  )
}

/* ===========================================================================
   THE CUSTOM RANGE — [from, to) as yyyy-MM-dd, computed in UTC so the range a
   reader in Riyadh picks is the range a reader anywhere gets. Weeks run
   Sun–Sat, the platform convention, and wear their real dates as labels.
   =========================================================================== */

interface CustomRange {
  from: string
  to: string
  /** What the reader picked, in words: "2026", "Aug 2026", "26 Jul – 1 Aug 2026". */
  label: string
}

function isoDay(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

function dayLabel(d: Date): string {
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
}

/** The Sun–Sat weeks touching a month, valued by their Sunday. */
function weeksOf(year: number, month: number): { value: string; label: string }[] {
  const firstOfNext = Date.UTC(year, month, 1)
  const s = new Date(Date.UTC(year, month - 1, 1))
  s.setUTCDate(s.getUTCDate() - s.getUTCDay())
  const out: { value: string; label: string }[] = []
  while (s.getTime() < firstOfNext) {
    const e = new Date(s)
    e.setUTCDate(e.getUTCDate() + 6)
    out.push({ value: isoDay(s), label: `${dayLabel(s)} – ${dayLabel(e)}` })
    s.setUTCDate(s.getUTCDate() + 7)
  }
  return out
}

/** Year / year+month / year+month+week, resolved to the [from, to) the API takes. */
function customRange(year: string, month: string, week: string): CustomRange {
  const y = Number(year)
  if (week) {
    const from = new Date(`${week}T00:00:00Z`)
    const last = new Date(from)
    last.setUTCDate(last.getUTCDate() + 6)
    const to = new Date(from)
    to.setUTCDate(to.getUTCDate() + 7)
    return { from: isoDay(from), to: isoDay(to), label: `${dayLabel(from)} – ${dayLabel(last)} ${last.getUTCFullYear()}` }
  }
  if (month) {
    const m = Number(month)
    return { from: isoDay(new Date(Date.UTC(y, m - 1, 1))), to: isoDay(new Date(Date.UTC(y, m, 1))), label: `${MONTHS[m - 1]} ${y}` }
  }
  return { from: `${y}-01-01`, to: `${y + 1}-01-01`, label: String(y) }
}

/** "Year to date" / "Month to date" / the picked range, for sentences. */
function periodPhrase(period: SalesPeriod, range: CustomRange | null): string {
  if (period === 'custom') return range?.label ?? 'Custom range'
  return period === 'mtd' ? 'Month to date' : 'Year to date'
}

/**
 * The selection as sentences — the head of every extract. The person reading
 * the FILE was not at the screen, so the file states its own frame: period,
 * currency, and every standing filter, chosen or not.
 */
function salesExportContext(
  period: SalesPeriod,
  range: CustomRange | null,
  currency: Currency,
  filters: SalesFilters,
  extra: string[] = [],
  /** Code → name, so an extract names the companies rather than coding them. */
  companyNames?: Map<string, string>,
): string[] {
  const companies = filters.company ?? []
  const named = companies.map((c) => companyNames?.get(c) ?? c)
  const chosen = [
    companies.length === 1
      ? `Company: ${named[0]}`
      : companies.length > 1
        ? `Companies (${companies.length}): ${named.join(', ')}`
        : undefined,
    filters.bu && `Business unit: ${filters.bu}`,
    filters.bt && `Business team: ${filters.bt}`,
    filters.sp && `Salesperson: ${filters.sp}`,
    filters.sup?.length && `Supplier: ${filters.sup.join(', ')}`,
    filters.reg && `Customer region: ${filters.reg}`,
    // Said as "contains" rather than "Customer: X", because it is a substring
    // and a file that claims one customer while holding several is a file
    // that will be quoted wrongly.
    filters.cust && `Customer name contains: ${filters.cust}`,
  ].filter(Boolean) as string[]
  return [
    `Period: ${periodPhrase(period, range)} — compared with the same span one year earlier`,
    `Currency: ${currencyNote(currency)}`,
    chosen.length ? `Filters: ${chosen.join(' · ')}` : 'Filters: none — everything the requester may see',
    ...extra,
  ]
}

/**
 * The tree's figures, as a row of things that can be asked about.
 *
 * It renders NOTHING at all unless the reader has developer mode on and these
 * keys are documented — `can` is false for everyone else, so the strip is
 * absent rather than empty. That keeps a tool for two reviewers out of the way
 * of the three hundred people who open this report to read it.
 */
function TreeLegend() {
  const { can } = useExplain()
  const keys: [string, string][] = [
    ['share', 'Share'],
    ['sales', 'Sales'],
    ['gp', 'Gross profit'],
    ['gpPct', 'GP %'],
    ['lines', 'Lines'],
  ]
  const shown = keys.filter(([k]) => can('tree', k))
  if (shown.length === 0) return null
  return (
    <p className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-(length:--text-small) text-(--color-ink-3)">
      <span className="eyebrow">The figures on each bar</span>
      {shown.map(([k, label]) => (
        <Explain key={k} surface="tree" measureKey={k}>
          {label}
        </Explain>
      ))}
    </p>
  )
}

/** Em dash for a masked or absent figure — never zero, never blank. */
const DASH = '—'

/** A percentage with one decimal, or the dash when unknown ("withheld" under the new model, M8). */
function pct(v: number | null, blank: string = DASH): string {
  return v === null ? blank : `${(v * 100).toFixed(1)}%`
}

/* ---------------------------------------------------------------------------
   WHAT A FIGURE IS IN — added 16 Sep 2026, because it was missing.

   Receivables and Material Planning both print the code beside the number;
   this report printed bare digits in Local mode, so "45.34B" could have been
   riyals, rupees or dirhams and nothing on the screen said which. The bug had
   two halves and the visible one was the smaller: `compact(v, currency)` drops
   the third argument that carries the code — but the procedure was returning
   no code to pass, so there was nothing to fix in the call alone.

   THE CODE BELONGS TO A ROW, NOT TO THE SCREEN. The fact holds 22 functional
   currencies and 18 units, one currency per company, so a reader scoped across
   companies really is adding riyals to dirhams and no single label is true of
   the whole screen. But a GRID ROW is a group, and a group is usually one
   company — so the tables ask their own procedures for a per-row code and
   print it, which is what 011 has always done for the receivables matrix.

   The KPI tiles get none, and that is measured rather than lazy: computing it
   in usp_Sales_Overview cost +1,799ms on the bypass path (640ms to 2,439ms,
   clone, 16 Sep 2026) because it meant reading two nvarchar columns across
   133,293 rows — on the one call the screen waits for before it can paint, to
   produce the word "mixed" for precisely the readers who waited longest.

   Anything that still needs ONE label for a whole grid folds the row answers
   below, in the browser, out of data it already has.
   --------------------------------------------------------------------------- */

/** One code when every row agrees, otherwise none. Receivables' KpiStrip rule. */
function foldCode(values: (string | null | undefined)[]): string | null {
  const set = new Set(values.filter((v): v is string => !!v))
  return set.size === 1 ? [...set][0]! : null
}

const EXPLORER_GRID_ID = 'sales-explorer'

/** One measure column of the Explorer grid: header, cell, tone and raw value. */
interface ExplorerColumn {
  key: string
  header: string
  kind: 'money' | 'pct' | 'int'
  /** What the heading needs to say on hover -- "Mixed Units", explained. */
  hint?: string
  /** `diffTone` is the row's own GP-difference colour, computed once per row. */
  tone: (n: ExplorerNode, diffTone: string) => string
  cell: (n: ExplorerNode) => ReactNode
  value: (n: ExplorerNode) => number | null
}

type ExplorerLayout = ReturnType<typeof useGridLayout<ExplorerColumn>>

/** The nine measure keys, which are also the node fields they read. */
type ExplorerMeasureKey =
  | 'sales' | 'salesPY' | 'gp' | 'gpPY' | 'gpDiff' | 'gpPct' | 'gpPctPY' | 'qty' | 'qtyPY'
/** Every heading sorts, the member column included — it sorts by the name shown. */
type ExplorerSortKey = 'member' | ExplorerMeasureKey
/** The ten, as data: a saved view's key is checked against them before it lands. */
const EXPLORER_SORT_KEYS: string[] = [
  'member', 'sales', 'salesPY', 'gp', 'gpPY', 'gpDiff', 'gpPct', 'gpPctPY', 'qty', 'qtyPY',
]

/* ===========================================================================
   THE STANDING FILTER BAR
   =========================================================================== */

/**
 * The customer filter: a text box, DEBOUNCED.
 *
 * Every other control on this bar commits on a click, so committing on the
 * spot is right for them. This one commits on a keystroke, and a standing
 * filter change refetches whichever of the six surfaces is open — so an
 * undebounced box would fire one report-wide read per character typed, six
 * ways, against a two-core database. 400ms is long enough that "Al Rajhi"
 * costs one read rather than eight, and short enough not to feel held back.
 *
 * The box keeps its own text so typing never stutters; only the COMMITTED
 * value goes up. The effect below re-syncs when the value changes from
 * outside — which is what Clear filters does, and what switching View As
 * does — because otherwise the box would still read "Al Rajhi" over a
 * report that had stopped filtering on it.
 */
function CustomerFilter({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const [text, setText] = useState(value)
  const committed = useRef(value)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (value === committed.current) return
    committed.current = value
    setText(value)
  }, [value])

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  const push = (next: string) => {
    setText(next)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const trimmed = next.trim()
      if (trimmed === committed.current) return
      committed.current = trimmed
      onChange(trimmed)
    }, 400)
  }

  return (
    <div className="flex min-w-[150px] flex-1 flex-col gap-1 max-w-[230px]">
      <Explain surface="filters" measureKey="cust" className="eyebrow">
        Customer contains
      </Explain>
      <input
        type="search"
        value={text}
        onChange={(e) => push(e.target.value)}
        /* Enter commits at once rather than waiting out the debounce — the
           reader has finished typing and said so. */
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          if (timer.current) clearTimeout(timer.current)
          const trimmed = text.trim()
          if (trimmed === committed.current) return
          committed.current = trimmed
          onChange(trimmed)
        }}
        placeholder="Any part of the name…"
        aria-label="Filter by customer name"
        className={inputClass}
      />
    </div>
  )
}

function SalesFilterBar({
  api,
  viewAs,
  value,
  onChange,
  onOptions,
}: {
  api: Api
  viewAs: string | null
  value: SalesFilters
  onChange: (f: SalesFilters) => void
  /** The full option list, surfaced so the page can offer the Year picker from the same scoped read. */
  onOptions?: (rows: SalesFilterRow[]) => void
}) {
  const [options, setOptions] = useState<SalesFilterRow[] | null>(null)

  useEffect(() => {
    let cancelled = false
    setOptions(null)
    api
      .salesFilterOptions()
      .then((r) => {
        if (cancelled) return
        setOptions(r)
        onOptions?.(r)
      })
      .catch(() => {
        if (cancelled) return
        setOptions([])
        onOptions?.([])
      })
    return () => {
      cancelled = true
    }
  }, [api, viewAs, onOptions])

  // A different pair of eyes is a different scope; a filter carried across
  // would silently show nothing and look like their book is empty.
  useEffect(() => {
    onChange({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAs])

  const of = (kind: SalesFilterRow['kind']) => (options ?? []).filter((o) => o.kind === kind)

  const dirty = Boolean(
    value.company?.length || value.bu || value.bt || value.sp || value.sup?.length || value.reg || value.cust,
  )

  /* SearchSelect rather than a native <select>: the salesperson list runs to
     hundreds of names and companies to forty — a scroll wheel is not a way to
     find Rana Aziz. */
  const select = (
    label: string,
    kind: SalesFilterRow['kind'],
    field: 'bu' | 'bt' | 'sp' | 'reg',
  ) => (
    <div className="flex min-w-[150px] flex-1 flex-col gap-1 max-w-[230px]">
      {/* A FILTER IS EXPLAINABLE TOO, and this is where the misreadings
          actually live. These six controls look identical and do not behave
          identically: Supplier and Region are exact matches, Customer below is
          a substring, Company is a one-of over a list of codes, and "Business
          Unit" matches Customer_Class -- an attribute of the customer, and NOT
          the column row security is decided on. None of that has ever been
          visible on the screen. The panel says which column, how it matches,
          and that the menu itself is scoped to the reader. */}
      <Explain surface="filters" measureKey={field} className="eyebrow">
        {label}
      </Explain>
      <SearchSelect
        label={label}
        /* A NAME NEEDS A WIDER PANEL THAN ITS TRIGGER. Salespeople run to 35
           characters and suppliers to 80; sized to the 250px trigger both cut
           ordinary entries. Business Unit, Business Team and Region are codes
           and keep the default. See NAME_PANEL_WIDTH for the measurements. */
        panelWidth={field === 'sp' ? NAME_PANEL_WIDTH : undefined}
        value={value[field] ?? ''}
        onChange={(v) => onChange({ ...value, [field]: v || undefined })}
        options={[
          { value: '', label: 'All' },
          ...of(kind).map((o) => ({ value: o.code ?? '', label: o.label || '(blank)' })),
        ]}
      />
    </div>
  )

  return (
    <div className="no-print flex shrink-0 flex-wrap items-end gap-3">
      {/* COMPANY IS MULTI-SELECT — most readers hold several companies and
          the question is usually "these three", not "this one" or "all forty".
          The code rides as the hint because it is what old hands type first. */}
      <div className="flex min-w-[170px] flex-1 flex-col gap-1 max-w-[250px]">
        <Explain surface="filters" measureKey="company" className="eyebrow">
          Company
        </Explain>
        <MultiSearchSelect
          label="Company"
          noun="companies"
          /* The same 40 companies Receivables lists, truncating the same way
             until 24 Sep 2026. A reader holding both reports should not meet
             two behaviours for one list. */
          panelWidth={NAME_PANEL_WIDTH}
          value={value.company ?? []}
          onChange={(v) => onChange({ ...value, company: v.length ? v : undefined })}
          /* Deduped by code before it reaches the picker: MultiSearchSelect keys
             its options by `value`, and two companies sharing a code — or two
             with a NULL code, both collapsing to '' — would render duplicate
             React keys. The Year picker already takes the same precaution. */
          options={[
            ...new Map(
              of('company').map((o) => [
                o.code ?? '',
                {
                  value: o.code ?? '',
                  label: o.label || '(blank)',
                  hint: o.code !== o.label ? (o.code ?? undefined) : undefined,
                },
              ]),
            ).values(),
          ]}
        />
      </div>
      {select('Business Unit', 'businessUnit', 'bu')}
      {select('Business Team', 'businessTeam', 'bt')}
      {select('Salesperson', 'salesperson', 'sp')}
      {/* SUPPLIER IS MULTI-SELECT TOO, since 25 Sep 2026, asked for on the Tree
          View and given to every tab because this is ONE bar: a control that
          took several values on one screen and one on the next would be two
          behaviours wearing the same label.

          The question it answers is "how are these three suppliers doing",
          which single-select could only ask three times. The list travels
          joined by U+001F rather than by a comma — a supplier is an ERP name
          and "Acme, Inc." split on a comma matches nothing. See the parameter
          note in 021 and NameList in SalesReportEndpoints. */}
      <div className="flex min-w-[170px] flex-1 flex-col gap-1 max-w-[250px]">
        <Explain surface="filters" measureKey="sup" className="eyebrow">
          Supplier
        </Explain>
        <MultiSearchSelect
          label="Supplier"
          noun="suppliers"
          /* 1,322 of them, and they are the longest names either report holds —
             NAME_PANEL_WIDTH is measured, not guessed. See ui.tsx. */
          panelWidth={NAME_PANEL_WIDTH}
          value={value.sup ?? []}
          onChange={(v) => onChange({ ...value, sup: v.length ? v : undefined })}
          /* Deduped for the reason the company list is: MultiSearchSelect keys
             its options by `value`, and the menu is built from a DISTINCT over
             a fact column that can repeat a name across companies. */
          options={[
            ...new Map(
              of('supplier').map((o) => [o.code ?? '', { value: o.code ?? '', label: o.label || '(blank)' }]),
            ).values(),
          ]}
        />
      </div>
      {select('Customer Region', 'region', 'reg')}
      {/* CUSTOMER IS TYPED, NOT PICKED — 9,742 distinct names measured
          10 Sep 2026. Every other control here is a menu because its
          dimension has tens or hundreds of members; this one has thousands,
          and a menu of thousands is a menu nobody can use. The label says
          "contains" so nobody expects it to match a whole name exactly. */}
      <CustomerFilter
        value={value.cust ?? ''}
        onChange={(v) => onChange({ ...value, cust: v || undefined })}
      />
      {dirty && (
        <button
          type="button"
          onClick={() => onChange({})}
          className="rounded-md border border-(--color-line-strong) px-3 py-2 text-(length:--text-small) font-semibold text-(--color-ink-2) hover:bg-(--color-surface-2) hover:text-(--color-ink)"
        >
          Clear filters
        </button>
      )}
    </div>
  )
}

/* ===========================================================================
   THE KPI ROW — the Power BI card strip, one audited read, with the counts'
   year bug fixed at the source (the DAX counted a month across every year).
   =========================================================================== */

function SalesKpis({
  api,
  viewAs,
  period,
  range,
  currency,
  filters,
  onData,
}: {
  api: Api
  viewAs: string | null
  period: SalesPeriod
  range: CustomRange | null
  currency: Currency
  filters: SalesFilters
  /** Lifts the loaded head to the page so the Explorer extract can include it. */
  onData?: (d: SalesOverview | null) => void
}) {
  /** The new permission model answered (M8): a blank figure is withheld, Partial marks a partly withheld one. */
  const [v2, setV2] = useState(false)
  const [data, setData] = useState<SalesOverview | null>(null)
  /* The failure is KEPT. It was swallowed into `data = null`, which the check
     below reads as "still loading", so a refused or failed call left the
     full-screen "Loading key figures" veil up for good, over the grid's own
     refusal panel. */
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setData(null)
    setFailure(null)
    onData?.(null)
    api
      .salesOverview({
        ...filters,
        period,
        from: range?.from,
        to: range?.to,
        currency,
      })
      .then((res) => {
        if (cancelled) return
        const r = res.data
        // A 204 resolves undefined; it is not a load in progress either.
        if (!r) {
          setFailure({ kind: 'error', title: 'The key figures came back empty.' })
          return
        }
        setData(r)
        setV2(res.vis !== null)
        onData?.(r)
      })
      .catch((e: unknown) => {
        if (!cancelled) setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not load the key figures.' })
      })
    return () => {
      cancelled = true
    }
  }, [api, viewAs, period, range, currency, filters, onData, attempt])

  /* A refusal is explained once, by the grid below (as Receivables' KpiStrip
     leaves it to its matrix); only a genuine error is said here. */
  if (failure) {
    return failure.kind === 'error' ? <FailureState failure={failure} onRetry={() => setAttempt((n) => n + 1)} /> : null
  }

  /* WHILE IT LOADS, SAY SO. This returned null until 16 Sep 2026, so the strip
     simply was not there — and this is the slowest call on the report, run
     again on every period toggle, currency switch and filter change. A reader
     watching four tiles vanish and reappear has no way to tell a slow query
     from a broken screen, and reported it as broken. Same standard loader the
     other reports use; the strip keeps its height so nothing below it jumps. */
  if (!data) {
    return (
      <div className="stat-strip">
        <div className="min-h-[4.5rem] flex-1">
          <Loading label="Loading key figures" />
        </div>
      </div>
    )
  }

  const span = period === 'custom' ? (range?.label ?? 'custom') : period === 'mtd' ? 'MTD' : 'YTD'
  /* UNDER THE NEW MODEL a blank figure is withheld, and one the row's Partial
     names leaves withheld values out: it keeps its value and the mark (M8). */
  const part = partialOf(data)
  const blank = v2 && data.invoices !== 0 ? WITHHELD : DASH
  const show = (v: number | null, name?: string) =>
    v === null ? blank : `${compact(v, currency)}${name && part.has(name) ? '*' : ''}`
  const full = (v: number | null, name?: string) =>
    v === null ? (v2 ? WITHHELD_TITLE : 'Not shown to you')
      : name && part.has(name) ? `${money(v, currency)}. ${PARTIAL_TITLE}.` : money(v, currency)

  /** "+12.4% vs last year" against the prior figure, or nothing when unknowable. */
  // Under the new model a change between partial figures is partial too (16d's review).
  const delta = (now: number | null, then: number | null, partial = false) => {
    if (now === null || then === null || then === 0) return undefined
    const d = (now - then) / Math.abs(then)
    const up = d >= 0
    return (
      <span className={up ? 'text-(--color-good)' : 'text-(--color-overdue)'}>
        {up ? '+' : ''}
        {(d * 100).toFixed(1)}%{partial ? '*' : ''} vs last year
      </span>
    )
  }
  /* Hoisted because the caption below needs to know whether the delta took the
     line: when it did, the currency has to be said beside it rather than
     instead of it. */
  const salesDelta = delta(data.sales, data.salesPY, part.has('sales') || part.has('salesPY'))
  const gpDelta = delta(data.gp, data.gpPY, part.has('gp') || part.has('gpPY'))

  return (
    <div className="stat-strip">
      {/* THE HEADLINE FIGURES, EXPLAINABLE AT LAST. These definitions were
          seeded in the first pass and sat unreachable, because the only way to
          reach them was a hand-threaded boolean nobody threaded this far. */}
      <StatTile
        surface="kpi"
        measureKey="sales"
        label={`Sales ${span}`}
        value={show(data.sales, 'sales')}
        fullValue={full(data.sales, 'sales')}
        secondary={
          <>
            {salesDelta ?? (currencyShort(currency))}
            {/* The legacy report's WK_SLS, restored by the coverage audit:
                said here so the week rides along whichever period is chosen. */}
            {data.salesWK !== null && (
              <>
                {' · '}
                {/* Its own figure with its own rule -- the ERP's week, not the
                    platform's, and it does NOT follow the period toggle above
                    the strip. Worth its own handle rather than riding the Sales
                    tile's, which obeys the toggle. */}
                <Explain surface="kpi" measureKey="salesWK">
                  this week {compact(data.salesWK, currency)}
                </Explain>
              </>
            )}
          </>
        }
        tone="accent"
        dense
      />
      <StatTile
        surface="kpi"
        measureKey="gp"
        label={`Gross profit ${span}`}
        value={show(data.gp, 'gp')}
        fullValue={full(data.gp, 'gp')}
        secondary={data.gp === null ? (v2 ? WITHHELD_TITLE : 'Requires the sales-cost permission') : gpDelta}
        dense
      />
      <StatTile
        surface="kpi"
        measureKey="gpPct"
        label={`GP % ${span}`}
        value={`${pct(data.gpPct, data.sales !== null && data.gp !== null ? DASH : blank)}${data.gpPct !== null && part.has('gpPct') ? '*' : ''}`}
        fullValue={data.gpPct === null ? (v2 ? WITHHELD_TITLE : 'Not shown to you')
          : `${(data.gpPct * 100).toFixed(2)}% of sales${part.has('gpPct') ? `. ${PARTIAL_TITLE}.` : ''}`}
        dense
        secondary={
          <>
            {data.gpPctPY === null ? null : `${pct(data.gpPctPY)}${part.has('gpPctPY') ? '*' : ''} last year`}
            {/* In USD mode the ratio stays the as-booked margin — the Power BI
                number — so dividing the two converted tiles beside it gives a
                DIFFERENT figure (mix-weighting). Both verification agents
                flagged the ambiguity; the caption resolves it. */}
            {isConverted(currency) && (
              <>
                {data.gpPctPY === null ? null : ' · '}
                as booked, not $GP ÷ $sales
              </>
            )}
          </>
        }
      />
      {/* ONE tile for all four counts, not two. Four tiles sit on one row at a
          1280px laptop exactly like the receivables strip; a fifth wrapped the
          strip to two rows and pushed the grid below the fold. The Power BI
          cards' counts all share one rule anyway — Samples excluded, year
          pinned — so they read as one fact with four faces. */}
      {/* ONE TILE, FOUR COUNTS, AND FOUR SEPARATE HANDLES. They share a rule --
          samples excluded, the year pinned -- but they count four DIFFERENT
          columns, and the differences are exactly what a reviewer needs: the
          customer count is on Customer_Number while every board and grouping on
          the report is on Customer_Name, and the product count is on
          Item_Number while the Explorer groups by description. Two of those
          pairs can legitimately disagree, so each figure explains itself rather
          than sharing the tile's. */}
      <StatTile
        surface="kpi"
        measureKey="customers"
        label={`Customers ${span}`}
        value={data.customers === null ? blank : String(data.customers)}
        secondary={
          <>
            <Explain surface="kpi" measureKey="salespeople">
              {data.salespeople ?? blank} salespeople
            </Explain>
            {' · '}
            <Explain surface="kpi" measureKey="items">
              {data.items ?? blank} products
            </Explain>
            {' · '}
            <Explain surface="kpi" measureKey="invoices">
              {data.invoices === null ? blank : data.invoices.toLocaleString('en-US')} invoices
            </Explain>
            {' — samples not counted'}
          </>
        }
        dense
      />
    </div>
  )
}

/* ===========================================================================
   THE EXPLORER — one drillable grid that replaced sixteen Power BI pages.

   Headers are never truncated and figures are never truncated; the table
   scrolls horizontally instead (the DataGrid rule, applied here by hand
   because these rows expand). A masked measure renders the dash on every
   row — a column of dashes says "not yours to see" better than any tooltip.
   =========================================================================== */

/** The chain the rows came from: the grouping, then levels 2 to 4. */
type ExplorerChain = [SalesDrillLevel, SalesDrillLevel, SalesDrillLevel, SalesDrillLevel]

interface ExplorerNode extends SalesExplorerRow {
  /** 0 is level 1. Four levels since 1 Oct 2026, so 3 is the deepest. */
  depth: 0 | 1 | 2 | 3
  /** The members drilled through to reach this row: l1, l2, l3. */
  path: [string?, string?, string?]
  open?: boolean
  children?: ExplorerNode[] | 'loading'
  /** The last child fetch failed. The row is left collapsed with `children`
      unset, so the next expand retries instead of showing a cached nothing.
      A string is the failure's correlation id, shown so it can be quoted. */
  childError?: boolean | string
  /** How many members the child level REALLY has. The procedure caps every
      level at 500, and a drilled level can be far longer -- Saudi Arabia >
      Customer is 3,239 (measured 29 Sep 2026) -- so the grid must say that
      it is showing the top of a list, not the whole of it. */
  childTotal?: number
}

/**
 * A row is expandable only if the child request will ACTUALLY go deeper.
 *
 * Two ways it would not, and both are reached with live data:
 *
 *  - A blank member. `member` is `string | null` and the grid already renders
 *    the blank case as "(blank)". The client drops a falsy l1/l2 and the API
 *    trims whitespace to NULL, and the procedure derives its level purely from
 *    which of @L1/@L2 are non-null — so a blank member re-requests the SAME
 *    level. Its "children" then come back as the entire top-level list and are
 *    assigned as this row's children: not a crash, but WRONG NUMBERS presented
 *    as a breakdown, which on a sales report is worse. Blank members occur in
 *    the live data (46 rows carry a blank Salesperson_Name).
 *  - Depth already the deepest (3 since the fourth level, 1 Oct 2026; 2
 *    before). `depth: (node.depth + 1) as 1 | 2 | 3` is an unchecked cast, so
 *    expanding a deepest row would mint a depth no level answers and
 *    re-request with the grandparent's path, nesting without bound.
 *
 * Same family as the ReceivablesMatrix walk that blanked the report: an identity
 * that is not guaranteed to advance. Decided on the client so the affordance
 * disappears rather than becoming silently inert.
 */
const EXPLORER_DEEPEST = 3
function explorerDrillable(n: ExplorerNode): boolean {
  return n.hasChildren && n.depth < EXPLORER_DEEPEST && !!(n.member && n.member.trim())
}

/**
 * THE READER'S DRILL ORDER (30 Sep 2026; a fourth level 1 Oct 2026). Level 1
 * is the Group-by pill and is only named here; levels 2 to 4 are pickers.
 * Four levels, always.
 *
 * The pickers cannot produce an invalid chain, so the procedure's fallback is
 * a guard and never the ordinary path: each picker offers everything but the
 * levels above it, and choosing a level that already sits further DOWN the
 * chain SWAPS the two rather than leaving a repeat for the server to quietly
 * correct. A chain equal to the grouping's default is stored as null, so a
 * reader who picks their way back to it is simply on the default -- a saved
 * view does not carry it and Reset does not appear.
 *
 * The pickers show the REQUEST, as the pill does; the rows are labelled from
 * the answer (shownChain), so for the moment in between the two may differ.
 */
function DrillOrder({
  dimension,
  drill,
  onDrill,
}: {
  dimension: SalesExplorerDimension
  drill: SalesDrillChoice | null
  onDrill: (d: SalesDrillChoice | null) => void
}) {
  const [, ...picked] = resolveSalesDrill(dimension, drill?.[0], drill?.[1], drill?.[2])
  const levels = picked as SalesDrillChoice
  const choose = (next: SalesDrillChoice) =>
    onDrill(sameDrill(next, SALES_DEFAULT_DRILL[dimension]) ? null : next)
  /** Level `at` (0 = level 2) becomes `v`; a deeper level already holding `v` takes the old value. */
  const pick = (at: number, v: SalesDrillLevel) => {
    const next = [...levels] as SalesDrillChoice
    const was = next.indexOf(v)
    if (was > at) next[was] = next[at]
    next[at] = v
    choose(next)
  }
  const opts = (except: SalesDrillLevel[]) => DRILL_OPTIONS.filter((o) => !except.includes(o.value))
  /* Compact: this line sits between the band and the grid, and height is the
     scarce axis on a laptop. IMPORTANT-FLAGGED, because an appended class does
     not beat inputClass by coming later in the string -- the stylesheet's own
     order decides, and there py-1.5 follows py-0.5. Measured 30 Sep 2026 at
     1536x864: unflagged, the pickers stayed 37px and the line grew from 41px
     to 50px. The sentence's own size, too, so the chosen level reads as a word
     in it rather than a form field dropped into it. */
  const compact = 'w-auto min-w-36 py-0.5! text-(length:--text-small)!'
  return (
    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      <span>Drill order:</span>
      <span className="font-medium text-(--color-ink-2)">{DRILL_LABEL[dimension]}</span>
      {levels.map((level, at) => (
        <Fragment key={at}>
          <span aria-hidden>→</span>
          <SearchSelect
            label={`Drill level ${at + 2}`}
            value={level}
            options={opts([dimension, ...levels.slice(0, at)])}
            onChange={(v) => pick(at, v as SalesDrillLevel)}
            buttonClassName={compact}
          />
        </Fragment>
      ))}
      {drill && (
        <button
          type="button"
          onClick={() => onDrill(null)}
          className="rounded px-1.5 py-0.5 font-semibold text-(--color-accent) hover:bg-(--color-surface) hover:underline"
          title={`Back to ${SALES_DEFAULT_DRILL[dimension].map((l) => DRILL_LABEL[l]).join(' → ')}`}
        >
          Reset
        </button>
      )}
    </span>
  )
}

function SalesExplorer({
  api,
  viewAs,
  period,
  range,
  currency,
  filters,
  companyNames,
  overview,
  exportRef,
  dimension,
  onDimension,
  drill,
  onDrill,
}: {
  api: Api
  viewAs: string | null
  period: SalesPeriod
  range: CustomRange | null
  currency: Currency
  filters: SalesFilters
  /** Code to name, so the extract names companies rather than coding them. */
  companyNames?: Map<string, string>
  overview: SalesOverview | null
  /** Where the card header's Excel/PDF buttons find this grid's builder. */
  exportRef?: { current: (() => ExportDoc | null) | null }
  /** The Group-by pill. Held by the page, which a saved view writes to. */
  dimension: SalesExplorerDimension
  onDimension: (d: SalesExplorerDimension) => void
  /** Levels 2 to 4 as the reader chose them, or null for the grouping's default. */
  drill: SalesDrillChoice | null
  onDrill: (d: SalesDrillChoice | null) => void
}) {
  /* THE CHAIN THE ROWS ON SCREEN WERE FETCHED FOR -- which is not always the
     one just picked. Between a click on "Customer Country" and its answer, the
     grid still holds the previous grouping's rows; labelling them from the
     picker would put business units under a "Customer Country" heading, and a
     drill on one of them would send a business unit's name as a country. So
     every label, the drill and the extract read THIS, and it changes in the
     same breath as the rows it describes (data-prep rule c).

     Since 30 Sep 2026 it is the whole chain, not only level 1, and it comes
     from the ANSWER: the procedure echoes the chain it actually used, so a
     drill order it had to correct is labelled as corrected, not as asked. */
  const [shownChain, setShownChain] = useState<ExplorerChain>(() =>
    resolveSalesDrill(dimension, drill?.[0], drill?.[1], drill?.[2]),
  )
  const shownDim = shownChain[0] as SalesExplorerDimension
  /* Every fetch takes a number; an answer whose number is no longer the
     latest is dropped. A slow Country response must not land on top of a
     quicker Customer Country one (data-prep rule d). */
  const generation = useRef(0)
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState('')
  const [data, setData] = useState<SalesExplorerResponse | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [roots, setRoots] = useState<ExplorerNode[]>([])
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [loading, setLoading] = useState(true)
  /* No initial key: the server returns each level already ranked by sales, and
     that ranking is the grid's natural order. The hook's third click drops
     back to it. */
  /* Named `toggleSort` because `toggle` below is the DRILL — the other thing a
     heading-shaped click could plausibly mean on this grid. */
  const { sort, setSort, toggle: toggleSort, ariaSort } = useGridSort<ExplorerSortKey>()
  /* THE SORT RIDES INSIDE A SAVED VIEW. An unrecognised key -- a column since
     dropped, or one this reader may not be shown -- falls back to this grid's
     own order rather than sorting by nothing. See useSortMirror. */
  useSortMirror(EXPLORER_GRID_ID, sort, (s) => {
    setSort(s.key !== null && EXPLORER_SORT_KEYS.includes(s.key) ? (s as SortState<ExplorerSortKey>) : { key: null, desc: true })
  })

  const load = useCallback(async () => {
    const mine = ++generation.current
    setLoading(true)
    setFailure(null)
    try {
      const { data: r, vis: rVis } = await api.salesExplorer({
        ...filters,
        period,
        from: range?.from,
        to: range?.to,
        dimension,
        /* Sent only when the reader chose them: the default is the
           procedure's own, and a request that omits both reads exactly as it
           did before 30 Sep 2026. */
        drill2: drill?.[0],
        drill3: drill?.[1],
        drill4: drill?.[2],
        search: applied || undefined,
        currency,
      })
      setV2(rVis !== null)
      if (mine !== generation.current) return
      setData(r)
      const echoed = r.drillChain
      setShownChain(
        echoed && echoed.length === 4 && echoed[0] === dimension
          ? [echoed[0], echoed[1], echoed[2], echoed[3]]
          : resolveSalesDrill(dimension, drill?.[0], drill?.[1], drill?.[2]),
      )
      setRoots(r.rows.map((row) => ({ ...row, depth: 0 as const, path: [] as ExplorerNode['path'] })))
    } catch (e: unknown) {
      if (mine !== generation.current) return
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
    } finally {
      if (mine === generation.current) setLoading(false)
    }
  }, [api, viewAs, period, range, currency, filters, dimension, drill, applied])

  useEffect(() => {
    void load()
  }, [load])

  const toggle = async (node: ExplorerNode) => {
    if (!explorerDrillable(node)) return
    /* Not while the grid is being replaced: the rows on screen belong to the
       previous question, and their children would be fetched for the new one
       -- a parent from one period or filter with children from another. The
       click is simply not taken; the new rows arrive a moment later. */
    if (loading) return
    if (node.open) {
      node.open = false
      setRoots((r) => [...r])
      return
    }
    node.open = true
    if (!node.children || node.children === 'loading') {
      node.children = 'loading'
      setRoots((r) => [...r])
      const mine = generation.current
      try {
        /* This row's path plus this row: the members above it, then its own at
           the position of its level. A level-1 row sends l1, a level-3 row
           sends l1, l2 and l3. */
        const steps: (string | undefined)[] = [...node.path]
        steps[node.depth] = node.member ?? undefined
        const path: ExplorerNode['path'] = [steps[0], steps[1], steps[2]]
        const [l1, l2, l3] = path
        const { data: r, vis: rVis } = await api.salesExplorer({
          ...filters,
          period,
          from: range?.from,
          to: range?.to,
          /* The chain THIS ROW came from, not whatever the pickers say now.
             All four are sent, so the procedure resolves the same chain it
             resolved for the parent. */
          dimension: shownDim,
          drill2: shownChain[1],
          drill3: shownChain[2],
          drill4: shownChain[3],
          l1,
          l2,
          l3,
          currency,
        })
        setV2(rVis !== null)
        /* The grid was replaced while this was in flight: the node is no
           longer on screen, and its answer belongs to nobody. */
        if (mine !== generation.current) return
        node.children = r.rows.map((row) => ({
          ...row,
          depth: (node.depth + 1) as 1 | 2 | 3,
          path,
        }))
        node.childTotal = r.totalRows
        node.childError = false
      } catch (e: unknown) {
        // Same rule as the answer: a failure for a grid that has since been
        // replaced must not take the new grid down with it.
        if (mine !== generation.current) return
        const f = e instanceof ApiError ? e.failure : null
        // A refusal is about the READER, not this branch (the procedure checks
        // access again on every drill, so a grant revoked mid-session lands
        // here). Replace the grid with the explanation and its reference, as a
        // refused first load does, rather than inviting retries that will
        // always be refused. Retry there reloads `roots` from scratch.
        if (f && (f.kind === 'forbidden' || f.kind === 'no-permissions' || f.kind === 'unauthenticated')) {
          setFailure(f)
          return
        }
        // Do NOT cache an empty list: that rendered a failed fetch as "this
        // row has no breakdown", indistinguishable from a true empty result,
        // and made it permanent — `children` was set, so no click would ever
        // ask again. Leave `children` unset so the next expand retries, close
        // the row so the reader sees it did not open, and say why.
        node.children = undefined
        node.childError = f?.kind === 'error' && f.correlationId ? f.correlationId : true
        node.open = false
      }
    }
    setRoots((r) => [...r])
  }

  // `keys` runs parallel to `flat`: each row's React key is its PARENT's key +
  // its own member + its position among its SIBLINGS. The flat index was used
  // before, and the flat list is not append-only — expanding any row shifted
  // the index of every row below it, re-keying and remounting them all. A
  // sibling index is stable under expansion elsewhere in the tree, and the
  // member alone is not enough: a level can return both a NULL and an
  // empty-string member, which would collide.
  const { flat, keys, cuts } = useMemo(() => {
    const out: ExplorerNode[] = []
    const ks: string[] = []
    /* WHERE A DRILLED LEVEL WAS CUT. Keyed by the flat index of the row the
       notice follows -- the last DESCENDANT of the parent, not its last child,
       so an open grandchild's rows stay above the line that closes its
       parent's list. Several lists can end on one row (a grandchild list
       closing inside a child list that also closes); innermost first. */
    const cutAt = new Map<number, { depth: number; shown: number; total: number }[]>()

    /**
     * THE SORT IS APPLIED PER LEVEL, INSIDE THE WALK — never to the flat list.
     *
     * This grid is a drill tree, and the only thing marking a row as a child
     * of the row above it is its indentation. Sorting the flattened output
     * would tear every child away from its parent and leave a column of
     * indents that pointed at whatever happened to precede them: a customer
     * filed under the wrong business unit, which on a sales report reads as a
     * fact rather than as a display bug. So each node's children are ordered
     * among THEMSELVES before the walk descends into them, exactly as
     * ReceivablesMatrix does. The nesting is untouched; only sibling order
     * moves.
     *
     * The tie-break is the member name, ascending both ways, so a level full
     * of equal figures (a column of dashes, say) does not reshuffle itself
     * between renders.
     */
    const order = (nodes: ExplorerNode[]): ExplorerNode[] => {
      const k = sort.key
      if (!k) return nodes
      const v = (n: ExplorerNode) => (k === 'member' ? n.member ?? '' : n[k])
      return [...nodes].sort((x, y) =>
        compareBy(v(x), v(y), sort.desc, () => (x.member ?? '').localeCompare(y.member ?? '')),
      )
    }

    const walk = (nodes: ExplorerNode[], parentKey: string) => {
      /* The key takes the node's position in the SERVER's order, not in the
         sorted one. A key built from the sorted position would change for every
         row the moment a heading was clicked, remounting the whole level — and
         the position is only here to separate a NULL member from an empty-string
         one, which the server's order does just as well. */
      const at = new Map<ExplorerNode, number>()
      nodes.forEach((n, j) => at.set(n, j))
      for (const n of order(nodes)) {
        const key = `${parentKey}/${n.member ?? ''}#${at.get(n) ?? 0}`
        out.push(n)
        ks.push(key)
        if (n.open && Array.isArray(n.children)) {
          walk(n.children, key)
          if (n.children.length > 0 && (n.childTotal ?? 0) > n.children.length) {
            const last = out.length - 1
            cutAt.set(last, [
              ...(cutAt.get(last) ?? []),
              { depth: n.depth + 1, shown: n.children.length, total: n.childTotal ?? 0 },
            ])
          }
        }
      }
    }
    walk(roots, '')
    return { flat: out, keys: ks, cuts: cutAt }
  }, [roots, sort])

  const spanLabel =period === 'custom' ? (range?.label ?? 'Custom') : period === 'mtd' ? 'MTD' : 'YTD'
  /* From the chain the rows came from -- see shownChain. */
  const chain = shownChain.map((l) => DRILL_LABEL[l])
  const gpMasked = roots.length > 0 && roots.every((r) => r.gp === null)

  /* PER ROW. Each grid row carries the code of its own group, so a business
     unit inside one company reads "SAR 86.9B" even where the selection above
     it spans twenty-one currencies and no single label would be true.

     The two folds below are for the things that are not a row: the extract's
     KPI block, and the quantity HEADING, which is one heading over a whole
     column and so can only say "Mixed Units" when the column is.

     The heading reads each row's `unitMixed` as well as its `unit` (4 Oct
     2026). Folding `unit` alone missed the commonest case: when EVERY row is
     itself a mixture -- every business unit sells in several units -- every
     unit is null, the fold found nothing, and the heading said plain "Qty
     YTD" over figures that add kilograms to litres. */
  /* UNDER THE NEW MODEL a blank figure is withheld, and one the row's Partial
     names leaves withheld values out: it keeps its value and the mark (M8). */
  const blank = v2 ? WITHHELD : DASH
  const mark = (row: { partial?: string | null }, name: string, s: string) =>
    s !== blank && partialOf(row).has(name) ? `${s}*` : s
  const num = (v: number | null, code: string | null) =>
    v === null ? blank : money(v, currency, code)
  const rootCode = foldCode(roots.map((r) => r.currencyCode))
  const qtyHead = quantityUnits(roots)

  /**
   * THE NINE MEASURE COLUMNS, defined once for the header, the rows and the
   * extract.
   *
   * They were three separate positional lists: an inline [key, header] array in
   * the header, ten hand-written <td>s in ExplorerRow, and a header-only array
   * in the extract. Three lists in the same order is fine until a reader can
   * REORDER them, at which point position stops meaning anything and the three
   * have to agree by key instead.
   *
   * Keys stay stable while labels move: "Sales YTD" and "Sales MTD" are the
   * same measure, and developer mode asks the server about the key.
   */
  const MEASURES: ExplorerColumn[] = [
    { key: 'sales', header: `Sales ${spanLabel}`, kind: 'money',
      tone: () => 'text-(--color-ink)', cell: (n) => mark(n, 'sales', num(n.sales, n.currencyCode)), value: (n) => n.sales },
    { key: 'salesPY', header: 'Sales LY', kind: 'money',
      tone: () => 'text-(--color-ink-3)', cell: (n) => mark(n, 'salesPY', num(n.salesPY, n.currencyCode)), value: (n) => n.salesPY },
    { key: 'gp', header: `GP ${spanLabel}`, kind: 'money',
      tone: () => 'text-(--color-ink)', cell: (n) => mark(n, 'gp', num(n.gp, n.currencyCode)), value: (n) => n.gp },
    { key: 'gpPY', header: 'GP LY', kind: 'money',
      tone: () => 'text-(--color-ink-3)', cell: (n) => mark(n, 'gpPY', num(n.gpPY, n.currencyCode)), value: (n) => n.gpPY },
    { key: 'gpDiff', header: 'GP Diff', kind: 'money',
      tone: (_n, diffTone) => `font-medium ${diffTone}`,
      cell: (n) => mark(n, 'gpDiff', n.gpDiff === null ? blank : `${n.gpDiff >= 0 ? '+' : '−'}${money(Math.abs(n.gpDiff), currency, n.currencyCode)}`),
      value: (n) => n.gpDiff },
    { key: 'gpPct', header: 'GP %', kind: 'pct',
      tone: () => 'text-(--color-ink)', cell: (n) => mark(n, 'gpPct', pct(n.gpPct, n.sales !== null && n.gp !== null ? DASH : blank)), value: (n) => n.gpPct },
    { key: 'gpPctPY', header: 'GP % LY', kind: 'pct',
      tone: () => 'text-(--color-ink-3)', cell: (n) => mark(n, 'gpPctPY', pct(n.gpPctPY, n.salesPY !== null && n.gpPY !== null ? DASH : blank)), value: (n) => n.gpPctPY },
    /* The unit rides the heading, not each cell: a column of thousands of rows
       would repeat it thousands of times, and unlike the tiles this figure HAS
       a column to look up at. "Mixed Units" when the column adds several, with
       the reason on hover -- see components/units.ts. */
    { key: 'qty', header: `Qty ${spanLabel}${qtyHead.suffix}`, hint: qtyHead.hint, kind: 'int',
      tone: () => 'text-(--color-ink-2)',
      cell: (n) => mark(n, 'qty', n.qty === null ? blank : n.qty.toLocaleString('en-US', { maximumFractionDigits: 0 })),
      value: (n) => n.qty },
    { key: 'qtyPY', header: `Qty LY${qtyHead.suffix}`, hint: qtyHead.hint, kind: 'int',
      tone: () => 'text-(--color-ink-3)',
      cell: (n) => mark(n, 'qtyPY', n.qtyPY === null ? blank : n.qtyPY.toLocaleString('en-US', { maximumFractionDigits: 0 })),
      value: (n) => n.qtyPY },
  ]

  const memberRef = useRef<HTMLTableCellElement | null>(null)
  const layout = useGridLayout(EXPLORER_GRID_ID, MEASURES, memberRef)
  const shown = layout.columns
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)

  /* EVERY HOOK RUNS BEFORE THE EARLY RETURN. This return sat above the four
     hooks just above it, so a failed request rendered fewer hooks than the
     render before; React threw, and a refused reader got the error boundary
     ("This view ran into a problem") instead of this panel explaining the
     refusal. Same bug as MaterialPlanningPage, 20 Sep 2026. Everything the
     return now waits for is safe on an empty grid (roots = []). */
  /* The header's buttons outlive this grid's contents, so a failed read must
     withdraw the builder: otherwise Excel would still hand over the LAST grid
     while this panel says it could not be loaded. Set again below, once there
     is something to extract. A ref write, not a hook -- the order rule above
     is untouched. */
  if (exportRef) exportRef.current = null
  /* A REFUSAL replaces the whole Explorer, as before: it is about the reader,
     and no grouping will answer it differently. An ordinary failure does NOT:
     it keeps the Group-by band, because it can belong to one grouping alone --
     a country grouping deployed ahead of its reference table fails while the
     other seven answer, and a scoped read can time out on one plan and not
     another. Replacing the band too left Retry as the only way on, and Retry
     re-sends the grouping that just failed (adversarial review, 29 Sep 2026). */
  const keepBand = failure?.kind === 'error'
  if (failure && !keepBand) return <FailureState failure={failure} onRetry={() => void load()} />
  /* The procedure orders by the sales figure only when the reader may see it;
     otherwise TOP (500) is cut from the names, A to Z. Every "top N" notice
     below says which, rather than claim a ranking that did not happen. */
  const salesMasked = roots.length > 0 && roots.every((r) => r.sales === null)
  const cutBy = salesMasked ? 'by name' : 'by sales'
  const cutWord = salesMasked ? 'first' : 'top'

  /* THE EXTRACT — the grid exactly as expanded on screen: the same visible
     rows in the same order (`flat` is already ordered per level by whatever
     heading the reader sorted), children indented under their parents, masked
     figures as dashes; the KPI tiles above the grid ride along. */
  const buildDoc = (): ExportDoc | null => {
    if (loading || flat.length === 0) return null
    const dimLabel = EXPLORER_DIMENSIONS.find((d) => d.value === shownDim)?.label ?? shownDim
    /* The sheet says how it is ordered, because it is read away from the
       screen that ordered it — and "within each level" is the part a reader
       needs, since the rows are a tree and not a list. */
    const sortLabel =
      sort.key === null
        ? null
        : sort.key === 'member'
          ? chain[0]
          : (MEASURES.find((c) => c.key === sort.key)?.header ?? sort.key)
    const deltaText = (now: number | null, then: number | null, partial = false) =>
      now !== null && then !== null && then !== 0
        ? `${now >= then ? '+' : ''}${(((now - then) / Math.abs(then)) * 100).toFixed(1)}%${partial ? '*' : ''} vs last year`
        : undefined
    return {
      report: 'Sales',
      page: 'Explorer',
      title: 'Sales Explorer',
      subtitle: `Grouped by ${dimLabel.toLowerCase()}, drill order ${chain.join(' → ')}.`,
      context: salesExportContext(period, range, currency, filters, [
        ...(applied ? [`Member search: contains "${applied}"`] : []),
        ...(sortLabel
          ? [`Sorted by ${sortLabel}, ${sort.desc ? 'descending' : 'ascending'} — applied within each level`]
          : []),
      ], companyNames),
      privileged: viewAs,
      currency,
      kpis: overview
        ? [
            {
              label: `Sales ${spanLabel}`,
              value: overview.sales === null ? blank : mark(overview, 'sales', compact(overview.sales, currency, rootCode)),
              /* The extract is read away from the screen that explains it, so
                 the currency is said outright rather than left to the figure —
                 a printed "45.34B" with no code is the bug this fixed, twice
                 over on paper. */
              secondary:
                deltaText(overview.sales, overview.salesPY, overview && (partialOf(overview).has('sales') || partialOf(overview).has('salesPY'))) ??
                (isConverted(currency) ? currencyShort(currency) : rootCode ? `${rootCode}, as booked` : currencyShort(currency)),
            },
            {
              label: `Gross profit ${spanLabel}`,
              value: overview.gp === null ? blank : mark(overview, 'gp', compact(overview.gp, currency, rootCode)),
              secondary:
                overview.gp === null
                  ? (v2 ? WITHHELD_TITLE : 'Requires the sales-cost permission')
                  : deltaText(overview.gp, overview.gpPY, overview && (partialOf(overview).has('gp') || partialOf(overview).has('gpPY'))) ??
                    (!isConverted(currency) ? rootCode ?? undefined : undefined),
            },
            {
              label: `GP % ${spanLabel}`,
              value: mark(overview, 'gpPct', pct(overview.gpPct, blank)),
              secondary:
                overview.gpPctPY === null
                  ? isConverted(currency) ? 'As booked, not converted GP ÷ converted sales' : undefined
                  : `${pct(overview.gpPctPY)} last year${isConverted(currency) ? ' · as booked' : ''}`,
            },
            {
              label: `Customers ${spanLabel}`,
              value: overview.customers === null ? blank : String(overview.customers),
              secondary: `${overview.salespeople ?? blank} salespeople · ${overview.items ?? blank} products · ${overview.invoices?.toLocaleString('en-US') ?? blank} invoices`,
            },
          ]
        : undefined,
      tables: [
        {
          /* The extract is the screen: the member, then whichever measures the
             reader has left showing, in their order. */
          columns: [{ header: chain[0] }, ...shown.map((c) => ({ header: c.header, kind: c.kind }))],
          /* A drilled level cut at 500 carries its notice into the file, on
             the same line as on screen: a sheet that silently lists the top
             of a list is a sheet somebody totals. Short, because it sits in
             the member column and would otherwise set that column's width. */
          rows: flat.flatMap<ExportRow>((n, i) => [
            {
              indent: n.depth,
              bold: n.depth === 0,
              cells: [n.member || '(blank)', ...shown.map((c) => {
                const v = c.value(n)
                const noRatio = (c.key === 'gpPct' && n.sales !== null && n.gp !== null) || (c.key === 'gpPctPY' && n.salesPY !== null && n.gpPY !== null)
                return v === null && v2 && !noRatio ? WITHHELD : v
              })],
            },
            ...(cuts.get(i) ?? []).map<ExportRow>((c) => ({
              indent: c.depth,
              cells: [
                `(${cutWord} ${c.shown.toLocaleString('en-US')} of ${c.total.toLocaleString('en-US')} ${cutBy})`,
                ...shown.map(() => ''),
              ],
            })),
          ]),
          note:
            (data && data.totalRows > (data.rows?.length ?? 0)
              ? `${salesMasked ? 'First' : 'Top'} ${data.rows.length.toLocaleString('en-US')} of ${data.totalRows.toLocaleString('en-US')} members ${cutBy}, as shown on screen. Samples ride the money and stay out of the counts.`
              : 'Samples ride the money and stay out of the counts.')
            + (v2 && flat.some((n) => partialOf(n).size > 0) ? ` ${PARTIAL_NOTE}` : ''),
        },
      ],
    }
  }
  if (exportRef) exportRef.current = failure ? null : buildDoc

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-(--color-line) px-5 py-(--band-y)">
        <div>
          <div className="eyebrow mb-1.5">Group by</div>
          <Segmented label="Dimension" value={dimension} onChange={onDimension} options={EXPLORER_DIMENSIONS} />
        </div>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            setApplied(search.trim())
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="eyebrow">Find a {chain[0].toLowerCase()}</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Contains…"
              className={inputClass}
            />
          </label>
          <button
            type="submit"
            className="rounded-md border border-(--color-line-strong) px-3 py-2 text-(length:--text-small) font-semibold text-(--color-ink-2) hover:bg-(--color-surface-2)"
          >
            Search
          </button>
        </form>
        {/* Excel and PDF live in the card header when the card offers them a
            place (exportRef, see SalesPage) -- nine pills and the search fill
            this band on a laptop, and a third group here wrapped it. Kept here
            only as the fallback for an Explorer rendered without that card. */}
        {!exportRef && (
          <div className="flex items-center gap-2 pb-0.5">
            <ExportButtons build={buildDoc} />
          </div>
        )}
        {/* Portals into the card header's slot beside Full screen, so it adds
            nothing to this band; it only draws a strip of its own when there
            is no card. */}
        <GridChrome api={layout} />
      </div>

      {/* An ordinary failure sits UNDER the band -- see keepBand. Picking
          another grouping clears it, because load() starts by doing so. */}
      {failure ? (
        <FailureState failure={failure} onRetry={() => void load()} />
      ) : (
      <>
      {/* The drill chain, said out loud: expanding rows walks these levels --
          and since 30 Sep 2026 the reader chooses levels 2 and 3 here. A div,
          not the <p> it was: the pickers open a panel, and a block inside a
          paragraph is invalid markup the browser silently re-parents. */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-1.5 text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
        {/* The other half of the .short-hide pair above. Where the card header
            is dropped, the reader is still told what span these figures cover
            and which currency they are booked in — the only part of that
            header that stated something the screen did not already say. */}
        <span className="short-only">
          {periodPhrase(period, range)} against the same span last year, in {currencyShort(currency)}.
        </span>
        <DrillOrder dimension={dimension} drill={drill} onDrill={onDrill} />
        <span>
        {gpMasked && (
          <>
            {' '}
            <span className="font-medium text-(--color-warn)">
              {v2 ? 'Gross profit is withheld from you here.' : 'Gross profit requires the sales-cost permission; those columns show a dash.'}
            </span>
          </>
        )}
        {data && data.totalRows > (data.rows?.length ?? 0) && (
          <>
            {' '}
            Showing the {cutWord} {data.rows.length.toLocaleString('en-US')} of{' '}
            {data.totalRows.toLocaleString('en-US')} {cutBy} — use the search to reach the rest.
          </>
        )}
        </span>
      </div>

      {loading && <Loading label="Loading sales…" />}

      {!loading && flat.length === 0 ? (
        <EmptyState
          title="No sales for this view"
          detail="An empty result for the data you are permitted to see — not a refusal. Try clearing a filter."
        />
      ) : (
        <div className="scroll-slim min-h-0 flex-1 overflow-auto">
          <table className="report-table w-full border-collapse text-(length:--text-body)">
            {/* --cell-y here, not --head-y as DataGrid uses. --head-y is 10px
                because DataGrid's header is body-sized and semibold; these
                headers are `eyebrow` — smaller and uppercase — and 10px made
                the sticky row 40px where it had been 36, which cost a tenth of
                a row at 1080. The extra air exists for larger type; this type
                is not larger. Measured 9 Sep 2026. */}
            <thead className="sticky top-0 z-10">
              <tr>
                {/* KEYED, not just labelled. The key is what developer mode
                    asks the server about, and it has to be stable while the
                    label moves with the period and the dimension -- "Sales YTD"
                    and "Sales MTD" are the same measure. */}
                {/* The member column is fixed furniture: it carries the
                    chevron, the stepped indent and the drill, its label moves
                    with the dimension, and a grid of figures addressed to
                    nothing is what hiding it would leave. The nine measures
                    beside it belong to the reader.

                    It sorts like the rest, by the name shown in it — within
                    each level, so a drilled-open branch stays under its parent.
                    `eyebrow` moved from the label onto this cell: it only ever
                    carried typography, all of it inherited. */}
                <th
                  ref={memberRef}
                  scope="col"
                  aria-sort={ariaSort('member')}
                  className="eyebrow sticky left-0 z-[1] whitespace-nowrap border-b border-(--color-line-strong) bg-(--color-surface) px-(--cell-x) py-(--cell-y) text-center"
                >
                  <SortHeader
                    label={chain[0]}
                    active={sort.key === 'member'}
                    desc={sort.desc}
                    onToggle={() => toggleSort('member')}
                    surface="explorer"
                    measureKey="member"
                    extra={{ Dimension: shownDim, 'Drill order': shownChain.join(' > '), ...(applied ? { Search: applied } : {}) }}
                  />
                </th>
                {shown.map((c) => {
                  const pin = layout.pinOf(c.key)
                  const w = layout.widthOf(c.key)
                  return (
                  <th
                    key={c.key}
                    ref={layout.headerRef(c.key)}
                    scope="col"
                    aria-sort={ariaSort(c.key as ExplorerSortKey)}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragKey(c.key) }}
                    onDragOver={(e) => { if (!dragKey || dragKey === c.key) return; e.preventDefault(); setOverKey(c.key) }}
                    onDrop={(e) => {
                      e.preventDefault()
                      if (dragKey && dragKey !== c.key) layout.move(dragKey, layout.indexOf(c.key))
                      setDragKey(null); setOverKey(null)
                    }}
                    onDragEnd={() => { setDragKey(null); setOverKey(null) }}
                    /* A FLOOR, NOT A CAP (4 Oct 2026). Every column here is a
                       figure, and a figure wider than the reader's stored width
                       -- "SAR 79,869,869" where the width was set over "$79.9M"
                       -- must widen its column, not run into the next one. See
                       rule 1 in gridLayout.tsx. */
                    style={{
                      width: w, minWidth: w,
                      left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                      right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                    }}
                    className={`eyebrow relative whitespace-nowrap border-b border-(--color-line-strong) bg-(--color-surface) px-(--cell-x) py-(--cell-y) text-center ${
                      pin ? 'sticky z-[2]' : ''
                    } ${overKey === c.key ? 'bg-(--color-navy-100)' : ''} ${dragKey === c.key ? 'opacity-50' : ''}`}
                  >
                    {/* SortHeader renders the `data-head-label` span the layout
                        measures, the label as the sort button, and the
                        explanation mark as a SIBLING of that button — never
                        wrapped around it, which would be a button inside a
                        button and would cost the column its click.

                        The mark appears only where this key has a definition
                        behind it AND the reader has the switch on. `extra` is
                        what only THIS component knows about the request: the
                        provider supplies the standing filters, the period and
                        the currency, and the grouping and the search live here. */}
                    <SortHeader
                      label={c.header}
                      hint={c.hint}
                      active={sort.key === c.key}
                      desc={sort.desc}
                      onToggle={() => toggleSort(c.key as ExplorerSortKey)}
                      surface="explorer"
                      measureKey={c.key}
                      extra={{ Dimension: shownDim, 'Drill order': shownChain.join(' > '), ...(applied ? { Search: applied } : {}) }}
                    />
                    <ResizeHandle
                      label={c.header}
                      widthOf={() => layout.headerWidth(c.key)}
                      minWidth={() => layout.minWidthOf(c.key)}
                      onResize={(px) => layout.resize(c.key, px)}
                      onAutoFit={() => layout.autoFit(c.key)}
                    />
                  </th>
                )})}
              </tr>
            </thead>
            <tbody>
              {flat.map((n, i) => (
                <Fragment key={keys[i]}>
                  <ExplorerRow node={n} onToggle={toggle} shown={shown} layout={layout} />
                  {(cuts.get(i) ?? []).map((c, j) => (
                    /* The drilled level was cut: say so at the foot of that
                       list, at its own indent, in the register of the
                       root-level "top 500 of N" line above the grid. The
                       drill carries no search, so the way on is the filter
                       bar, and the line says that rather than "search". */
                    <tr key={`cut-${j}`}>
                      <td
                        colSpan={shown.length + 1}
                        className="px-(--cell-x) py-(--cell-y) text-(length:--text-small) italic text-(--color-ink-3)"
                        style={{ paddingLeft: `${c.depth * 18 + 32}px` }}
                      >
                        Showing the {cutWord} {c.shown.toLocaleString('en-US')} of {c.total.toLocaleString('en-US')}{' '}
                        {cutBy} — narrow with the filters above to reach the rest.
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

    </div>
  )
}

function ExplorerRow({
  node,
  onToggle,
  shown,
  layout,
}: {
  node: ExplorerNode
  /* The row renders the measure cells, so it takes the same layout the header
     used — otherwise the two would disagree the moment a column moves. The
     formatters (num, currency) now live in the column definitions, which is why
     the row no longer takes them. */
  shown: ExplorerColumn[]
  layout: ExplorerLayout
  onToggle: (n: ExplorerNode) => void
}) {
  const diffTone =
    node.gpDiff === null
      ? 'text-(--color-ink-3)'
      : node.gpDiff >= 0
        ? 'text-(--color-good)'
        : 'text-(--color-overdue)'

  return (
    <>
      {/* scroll-mt matches the sticky header's height: anything that scrolls a
          row into view (keyboard focus, a test, a deep link) would otherwise
          park it UNDER the opaque sticky thead, where it renders but cannot be
          clicked. Found by the browser check, kept for every future reader. */}
      <tr className="scroll-mt-10 border-b border-(--color-line) hover:bg-(--color-surface-2)">
        {/* cell-member: the drill column keeps its left edge. It carries the
            chevron and a per-level indent, and the indent IS the hierarchy —
            centring it would leave every generation floating in its own row. */}
        <td className="cell-member px-(--cell-x) py-(--cell-y)">
          <span className="flex items-center gap-1.5" style={{ paddingLeft: `${node.depth * 18}px` }}>
            {explorerDrillable(node) ? (
              <button
                type="button"
                onClick={() => onToggle(node)}
                aria-expanded={node.open ?? false}
                aria-label={node.open ? 'Collapse' : 'Expand'}
                className="grid h-5 w-5 shrink-0 place-items-center rounded text-(--color-ink-3) hover:bg-(--color-line) hover:text-(--color-ink)"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden className={`transition-transform ${node.open ? 'rotate-90' : ''}`}>
                  <path d="M3 1l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : (
              <span className="w-5 shrink-0" />
            )}
            <span className={node.depth === 0 ? 'font-medium text-(--color-ink)' : 'text-(--color-ink-2)'}>
              {node.member || '(blank)'}
            </span>
            {node.childError && (
              <span className="text-(length:--text-small) text-(--color-overdue)">
                breakdown failed to load
                {typeof node.childError === 'string' && ` (reference ${node.childError})`} — expand to retry
              </span>
            )}
          </span>
        </td>
        {shown.map((c) => {
          const pin = layout.pinOf(c.key)
          const w = layout.widthOf(c.key)
          return (
            <td
              key={c.key}
              // No maxWidth: the stored width is a floor for a figure.
              style={{
                width: w,
                left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
              }}
              className={`num whitespace-nowrap px-(--cell-x) py-(--cell-y) text-right ${c.tone(node, diffTone)} ${
                pin ? 'sticky z-[1] bg-(--color-surface)' : ''
              }`}
            >
              {c.cell(node)}
            </td>
          )
        })}
      </tr>
      {node.open && node.children === 'loading' && (
        <tr>
          <td colSpan={10} className="px-(--cell-x) py-(--cell-y) text-(length:--text-small) text-(--color-ink-3)" style={{ paddingLeft: `${(node.depth + 1) * 18 + 12}px` }}>
            Loading…
          </td>
        </tr>
      )}
    </>
  )
}

/* ===========================================================================
   THE LEADERBOARDS — "By BU Backup" reborn as six honest little tables.
   =========================================================================== */

/** The four ranked dimensions. `singular` titles the member column in an extract. */
const BOARD_DIMS = [
  { key: 'customer', label: 'Customers', singular: 'Customer' },
  { key: 'item', label: 'Products', singular: 'Product' },
  { key: 'salesperson', label: 'Salespeople', singular: 'Salesperson' },
  { key: 'supplier', label: 'Suppliers', singular: 'Supplier' },
] as const

function SalesLeaderboards({
  api,
  viewAs,
  period,
  range,
  currency,
  filters,
  companyNames,
}: {
  api: Api
  viewAs: string | null
  period: SalesPeriod
  range: CustomRange | null
  currency: Currency
  filters: SalesFilters
  /** Code to name, so the extract names companies rather than coding them. */
  companyNames?: Map<string, string>
}) {
  /* No code here: the leaderboard procedure no longer returns one. It CROSS
     APPLYs the fact across eight board dimensions, so every column it reads is
     read eight times — adding the currency took it from 1,093ms to 2,228ms for
     a label beside a top-five list. Measured 16 Sep 2026. */
  const [rows, setRows] = useState<SalesBoardRow[] | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  const load = useCallback(async () => {
    setRows(null)
    setFailure(null)
    try {
      const res = await api.salesLeaderboard({ ...filters, period, from: range?.from, to: range?.to, currency })
      setRows(res.data)
      setV2(res.vis !== null)
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
    }
  }, [api, viewAs, period, range, currency, filters])

  useEffect(() => {
    void load()
  }, [load])

  if (failure) return <FailureState failure={failure} onRetry={() => void load()} />
  if (!rows) return <Loading label="Ranking…" />

  if (rows.length === 0)
    return (
      <EmptyState
        title="Nothing to rank"
        detail={v2 ? 'No sales in this view that your access shows you.' : 'No sales in this view for the data you are permitted to see.'}
      />
    )

  const hasGp = rows.some((r) => r.board.endsWith('-gp'))

  const board = (dim: string, measure: 'sales' | 'gp') => rows.filter((r) => r.board === `${dim}-${measure}`)

  /* One band per measure. A masked caller gets the sales band alone — the
     gross-profit band is ABSENT, not empty, because a row of headings with
     nothing under them still says the boards exist. */
  const sections: { measure: 'sales' | 'gp'; label: string }[] = [
    { measure: 'sales', label: 'By sales' },
    ...(hasGp ? [{ measure: 'gp' as const, label: 'By gross profit' }] : []),
  ]

  /* THE EXTRACT — each visible board as its own small table, GP boards only
     when they are on screen (absent boards stay absent: the ordering itself
     is what the permission protects). */
  const buildDoc = (): ExportDoc | null => {
    if (rows.length === 0) return null
    const boards: { title: string; dim: string; member: string; measure: 'sales' | 'gp' }[] = [
      ...BOARD_DIMS.map((d) => ({ title: `${d.label} by sales`, dim: d.key as string, member: d.singular, measure: 'sales' as const })),
      ...(hasGp
        ? BOARD_DIMS.map((d) => ({ title: `${d.label} by gross profit`, dim: d.key as string, member: d.singular, measure: 'gp' as const }))
        : []),
    ]
    return {
      report: 'Sales',
      page: 'Leaderboards',
      title: `Top five, ${periodPhrase(period, range).toLowerCase()}`,
      subtitle: 'Customers, products, salespeople and suppliers, ranked by sales and by gross profit. Ties widen a list.',
      context: salesExportContext(period, range, currency, filters, [
        ...(hasGp ? [] : [v2 ? 'The gross-profit boards are withheld from you and are not part of this extract.'
          : 'The gross-profit boards require the sales-cost permission and are not part of this extract.']),
      ], companyNames),
      privileged: viewAs,
      currency,
      tables: boards.map((b) => ({
        title: b.title,
        columns: [
          { header: '#', kind: 'int' },
          { header: b.member },
          { header: b.measure === 'gp' ? 'Gross profit' : 'Sales', kind: 'money' },
        ],
        rows: board(b.dim, b.measure).map<ExportRow>((r) => ({
          cells: [r.rank, r.member || '(blank)', r.value === null && v2 ? WITHHELD : r.value],
        })),
        ...(v2 && board(b.dim, b.measure).some((r) => partialOf(r).has('value')) ? { note: PARTIAL_NOTE } : {}),
      })),
    }
  }

  return (
    /* overflow-y-auto is a safety net for a very short window, not the
       expected state: eight boards are sized to sit on one screen. */
    <div className="scroll-slim min-h-0 flex-1 overflow-y-auto px-5 py-3">
      {!hasGp && (
        <p className="mb-4 rounded-md border border-(--color-line) bg-(--color-surface-2) px-3 py-2 text-(length:--text-small) text-(--color-ink-3)">
          {v2 ? 'The gross-profit boards are withheld from you, so only the sales boards are' : 'The gross-profit boards require the sales-cost permission, so only the sales boards are'}
          drawn — a ranking by margin would disclose the margin order.
        </p>
      )}
      {/* BANDED BY MEASURE — the layout the fourth dimension forced.
          With three dimensions, six cards in a three-column grid put every
          sales board on one row and every GP board on the next, and the
          grouping READ even though nothing stated it. A fourth dimension
          broke that silently: eight cards over three columns spilled
          "Suppliers by sales" onto the same row as two gross-profit boards,
          so a row stopped being a group while still looking like one.

          Stating the measure on a band fixes it at the source rather than by
          picking a column count that happens to divide: the meaning now
          survives every reflow, and a fifth dimension would not disturb it. */}
      <div className="flex flex-col gap-3">
        {sections.map((s, i) => (
          <section key={s.measure}>
            {/* The extract pair rides the FIRST band's rule rather than
                claiming a row of its own — that row was the difference
                between eight boards fitting one screen and not. */}
            <div className="mb-2 flex items-center gap-3">
              {/* EACH HANDLE EXPLAINS WHAT IT ACTUALLY NAMES. A board row is
                  not a button, but wrapping forty member names and forty rank
                  numbers would bury the data under affordances -- so the two
                  labels that already exist carry it:

                    the band  "By sales"    names the MEASURE  -> value
                    the title "Customers"   names the MEMBER   -> member

                  and the ranking rule (ties SHARE a rank, which is DENSE_RANK
                  and matches the Power BI report this replaced) rides beside
                  the band as a mark, because it belongs to neither label. */}
              <h3 className="eyebrow">
                <Explain surface="leaderboard" measureKey="value" extra={{ Measure: s.label }}>
                  {s.label}
                </Explain>
              </h3>
              <ExplainMark surface="leaderboard" measureKey="rank" label="Rank" />
              <span aria-hidden className="h-px flex-1 bg-(--color-line)" />
              {i === 0 && <ExportButtons build={buildDoc} />}
            </div>
            {/* Two columns or four, never three: a four-card band has to land
                as a clean rectangle at every width, and three would leave one
                card orphaned on a row of its own. */}
            {/* @-VARIANTS, NOT xl:. These measure the content box; xl: measured the
                window, which sat 304px wider, so this switched to its widest
                layout at exactly the width where there was least room. Measured
                8 Sep 2026: at a 1279px window these boards were 2 columns of
                453px; at 1280px, 4 columns of 219px. One pixel, 67% narrower. */}
            <div className="grid gap-4 @3xl:grid-cols-2 @6xl:grid-cols-4">
              {BOARD_DIMS.map((d) => (
                <Board
                  key={`${d.key}-${s.measure}`}
                  title={d.label}
                  rows={board(d.key, s.measure)}
                  currency={currency}
                  measure={s.measure}
                  v2={v2}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

/**
 * One board. The title is the DIMENSION alone — the band above states the
 * measure, so repeating "by sales" on four cards in a row would be four
 * copies of one fact.
 */
function Board({ title, rows, currency, measure, v2 }: {
  title: string; rows: SalesBoardRow[]; currency: Currency; measure: 'sales' | 'gp'
  /** The new permission model answered (M8): a blank value is withheld, Partial's 'value' marks a partial one. */
  v2: boolean
}) {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.value ?? 0)))
  return (
    <section className="rounded-md border border-(--color-line) bg-(--color-surface) p-3 shadow-(--shadow-card)">
      {/* The title names the DIMENSION being ranked, so it explains the member:
          which of the four columns this board reads, and that all four boards
          come from ONE pass over the fact table rather than four queries that
          might disagree. */}
      <h4 className="mb-2 text-(length:--text-base) font-semibold text-(--color-ink)">
        <Explain surface="leaderboard" measureKey="member" extra={{ Board: title, Measure: measure === 'gp' ? 'Gross profit' : 'Sales' }}>
          {title}
        </Explain>
      </h4>
      {rows.length === 0 ? (
        <p className="text-(length:--text-small) text-(--color-ink-3)">Nothing in this view.</p>
      ) : (
        <ol className="flex flex-col gap-1">
          {rows.map((r) => {
            /* Ties share rank 1 — DENSE_RANK, the Power BI behaviour — so
               "the leader" can legitimately be more than one row. */
            const leads = r.rank === 1
            return (
              <li key={`${r.rank}:${r.member}`} className="grid grid-cols-[16px_1fr_auto] items-center gap-2">
                <span
                  className={`num text-(length:--text-small) font-semibold ${
                    leads ? 'text-(--color-navy-700)' : 'text-(--color-ink-3)'
                  }`}
                >
                  {r.rank}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-(length:--text-base) text-(--color-ink)" title={r.member ?? ''}>
                    {r.member || '(blank)'}
                  </span>
                  {/* The bar carries the comparison; the figure carries the
                      value. The leader's bar is the brand navy and the rest
                      are lighter, so the eye lands on first place without
                      having to read a single number. */}
                  <span aria-hidden className="mt-1 block h-[3px] w-full overflow-hidden rounded-full bg-(--color-line)">
                    <span
                      className={`block h-full rounded-full ${leads ? 'bg-(--color-navy-700)' : 'bg-(--color-navy-400)'}`}
                      style={{ width: `${(Math.abs(r.value ?? 0) / max) * 100}%` }}
                    />
                  </span>
                </span>
                <span
                  className="num whitespace-nowrap text-(length:--text-base) font-semibold text-(--color-ink)"
                  title={r.value === null ? (v2 ? WITHHELD_TITLE : undefined)
                    : partialOf(r).has('value') ? `${money(r.value, currency)}. ${PARTIAL_TITLE}.` : money(r.value, currency)}
                >
                  {r.value === null ? (v2 ? WITHHELD : DASH) : `${compact(r.value, currency)}${partialOf(r).has('value') ? '*' : ''}`}
                </span>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}

/* ===========================================================================
   THE MONTHLY TREND — V1's PBI_Sales_Monthly, the one thing V2 dropped,
   drawn in the platform's own chart idiom: plain SVG, two series, a
   crosshair, and a summary table. No charting library — the receivables
   trend explains why, and the same argument holds here.
   =========================================================================== */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Every heading of the month panel sorts, the Month column included. */
type TrendSortKey = 'month' | 'thisYear' | 'lastYear' | 'change'

/**
 * One month of the panel beside the plot.
 *
 * `i` IS THE MONTH — its position in the year, 0-11 — and it is carried on the
 * row rather than taken from the row's position in the list. The chart is
 * indexed by month, and the hover that lights a row also lights that month's
 * point on the plot; once the table can be sorted, row position and month stop
 * being the same number, and a hover read off the position would light January
 * on the chart because December happened to be drawn first.
 */
interface TrendMonthRow {
  i: number
  month: string
  a: number | null
  b: number | null
  d: number | null
  /** Under the new model, the month's figure is withheld, not a month without sales (M8). */
  aWithheld: boolean
  bWithheld: boolean
}

function SalesTrend({
  api,
  viewAs,
  currency,
  filters,
  companyNames,
}: {
  api: Api
  viewAs: string | null
  currency: Currency
  filters: SalesFilters
  /** Code to name, so the extract names companies rather than coding them. */
  companyNames?: Map<string, string>
}) {
  /* No code here: a trend row is a MONTH, and a month spans every company in
     the selection, so its currency is the whole selection's — which is the one
     question that has no single answer. Nothing to print. */
  const [rows, setRows] = useState<SalesTrendRow[] | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [measure, setMeasure] = useState<'sales' | 'gp'>('sales')
  const [hover, setHover] = useState<number | null>(null)
  /* The sort lives HERE rather than in TrendTable because the extract below
     has to ship the same order the panel renders, and only this component
     builds the extract. No initial key: the natural order is the calendar. */
  const { sort, toggle, ariaSort } = useGridSort<TrendSortKey>()

  const load = useCallback(async () => {
    setRows(null)
    setFailure(null)
    try {
      const res = await api.salesTrend({ ...filters, currency })
      setRows(res.data)
      setV2(res.vis !== null)
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
    }
  }, [api, viewAs, currency, filters])

  useEffect(() => {
    void load()
  }, [load])

  if (failure) return <FailureState failure={failure} onRetry={() => void load()} />
  if (!rows) return <Loading label="Loading two years of months…" />

  const gpAvailable = rows.some((r) => r.gp !== null)
  const m = gpAvailable ? measure : 'sales'

  const thisYear = Math.max(0, ...rows.map((r) => r.year))
  const lastYear = thisYear - 1
  // Under the new model a blank month is withheld: a gap, never a 0 (16d's review).
  const value = (r: SalesTrendRow) => {
    const v = m === 'gp' ? r.gp : r.sales
    return v === null && v2 ? null : (v ?? 0)
  }
  const withheldIn = (year: number) =>
    new Set(rows.filter((r) => r.year === year && v2 && (m === 'gp' ? r.gp : r.sales) === null).map((r) => r.month - 1))
  const whCur = withheldIn(thisYear)
  const whPrev = withheldIn(lastYear)
  const allWithheld = v2 && rows.length > 0 && rows.every((r) => (m === 'gp' ? r.gp : r.sales) === null)

  const seriesOf = (year: number) => {
    const out = Array<number | null>(12).fill(null)
    for (const r of rows) if (r.year === year) out[r.month - 1] = value(r)
    return out
  }
  const cur = seriesOf(thisYear)
  const prev = seriesOf(lastYear)

  const empty = rows.length === 0 || (cur.every((v) => !v) && prev.every((v) => !v))

  /* The comparison the summary makes: the months THIS year actually has,
     against the very same month numbers last year. Comparing a part-year
     against a whole one would be the oldest wrong number in reporting. */
  const present = cur.map((v, i) => (v === null ? -1 : i)).filter((i) => i >= 0)
  const spanLabel =
    present.length === 0
      ? ''
      : present.length === 1
        ? MONTHS[present[0]]
        : `${MONTHS[present[0]]}–${MONTHS[present[present.length - 1]]}`
  const curTotal = present.reduce((s, i) => s + (cur[i] ?? 0), 0)
  const prevTotal = present.reduce((s, i) => s + (prev[i] ?? 0), 0)
  /* UNDER THE NEW MODEL a month can leave out what a piece of the reader's access
     withholds, and the lines can only draw what shows (M8): a total that adds such
     a month is marked, and a note above the chart says so. */
  const monthPartial = (year: number, i: number) =>
    v2 && rows.some((r) => r.year === year && r.month === i + 1
      && ((m === 'gp' ? r.gp : r.sales) === null || partialOf(r).has(m)))
  const curPartial = whCur.size > 0 || present.some((i) => monthPartial(thisYear, i))
  const prevPartial = present.some((i) => whPrev.has(i) || monthPartial(lastYear, i))
  const marked = (s: string, partial: boolean) => (partial ? `${s}*` : s)
  const markedFull = (s: string, partial: boolean) => (partial ? `${s}. ${PARTIAL_TITLE}.` : s)

  /**
   * THE TWELVE MONTHS, IN THE ORDER THE PANEL SHOWS THEM.
   *
   * The natural order is the calendar, which is what `MONTHS` already is, so
   * with no column chosen this is `MONTHS` untouched — and the hook's third
   * click returns to it. A trend read out of calendar order is not a trend, so
   * the ranking has to be the thing a reader comes back to rather than
   * something they have to rebuild by hand.
   *
   * THE MONTH COLUMN SORTS BY THE CALENDAR, NOT BY THE LETTERS OF ITS LABEL.
   * The label is a month name and its whole meaning is its place in the year;
   * ordering it alphabetically would give Apr, Aug, Dec, Feb — a sequence that
   * is neither a calendar nor anything else. Descending is therefore Dec → Jan,
   * which is the order somebody looking for the latest month wants.
   */
  const monthRows: TrendMonthRow[] = MONTHS.map((mo, i) => {
    const av = cur[i]
    const bv = prev[i]
    return { i, month: mo, a: av, b: bv, d: av !== null && bv !== null ? av - bv : null, aWithheld: whCur.has(i), bWithheld: whPrev.has(i) }
  })
  const orderedMonths: TrendMonthRow[] = sort.key
    ? [...monthRows].sort((x, y) => {
        const k = sort.key as TrendSortKey
        const v = (r: TrendMonthRow) =>
          k === 'month' ? r.i : k === 'thisYear' ? r.a : k === 'lastYear' ? r.b : r.d
        // The calendar is the tie-break: two months with the same figure (or
        // both masked) stay in the order the year puts them in.
        return compareBy(v(x), v(y), sort.desc, () => x.i - y.i)
      })
    : monthRows

  /* THE EXTRACT — the chart rasterized from the very SVG on screen, then the
     same month-by-month table with the same change column. Async because the
     rasterizing is; a failed rasterize ships the table alone. */
  const buildDoc = async (): Promise<ExportDoc | null> => {
    if (empty) return null
    const measureLabel = m === 'gp' ? 'Gross profit' : 'Sales'
    const chart = await chartFromSvg(
      document.getElementById('sales-trend-svg') as SVGSVGElement | null,
      `${measureLabel} by month — ${thisYear} (navy) against ${lastYear}.`,
    )
    return {
      report: 'Sales',
      page: 'Monthly Trend',
      title: `Monthly ${measureLabel.toLowerCase()}, ${thisYear} against ${lastYear}`,
      subtitle: 'Real monthly amounts from the invoice dates — not year-to-date lines.',
      context: salesExportContext(
        'ytd',
        null,
        currency,
        filters,
        [
          `Measure: ${measureLabel}`,
          /* Said outright: a sheet of months not in calendar order looks like a
             sheet with its months scrambled unless it says why. */
          ...(sort.key
            ? [
                `Sorted by ${
                  sort.key === 'month'
                    ? 'Month'
                    : sort.key === 'thisYear'
                      ? String(thisYear)
                      : sort.key === 'lastYear'
                        ? String(lastYear)
                        : 'Change'
                }, ${sort.desc ? 'descending' : 'ascending'} — not calendar order`,
              ]
            : []),
        ],
        companyNames,
      ).filter(
        (l) => !l.startsWith('Period:'), // the months ARE the periods; a period line would mislead
      ),
      privileged: viewAs,
      currency,
      chart,
      tables: [
        {
          columns: [
            { header: 'Month' },
            { header: String(thisYear), kind: 'money' },
            { header: String(lastYear), kind: 'money' },
            { header: 'Change', kind: 'money' },
          ],
          /* THE EXTRACT IS THE SCREEN: the months in the order the panel shows
             them, which is the calendar until the reader sorts by a column. */
          rows: orderedMonths.map<ExportRow>((r) => ({ cells: [
            r.month, r.aWithheld ? WITHHELD : r.a, r.bWithheld ? WITHHELD : r.b, r.aWithheld || r.bWithheld ? WITHHELD : r.d,
          ] })),
          note: [
            gpAvailable ? undefined
              : v2 ? 'Gross profit is withheld from you and is not part of this extract.'
              : 'Gross profit requires the sales-cost permission and is not part of this extract.',
            curPartial || prevPartial ? PARTIAL_NOTE : undefined,
          ].filter(Boolean).join(' ') || undefined,
        },
      ],
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-(--color-line) px-5 py-4">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div>
            <div className="eyebrow mb-1.5">Measure</div>
            <Segmented
              label="Measure"
              value={m}
              onChange={(v) => setMeasure(v)}
              options={
                gpAvailable
                  ? [
                      { value: 'sales', label: 'Sales' },
                      { value: 'gp', label: 'Gross profit' },
                    ]
                  : [{ value: 'sales', label: 'Sales' }]
              }
            />
          </div>
          {!gpAvailable && (
            <p className="pb-1 text-(length:--text-small) text-(--color-ink-3)">
              {v2 ? 'Gross profit is withheld from you here.' : 'Gross profit requires the sales-cost permission.'}
            </p>
          )}
        </div>
        <div className="pb-0.5">
          <ExportButtons build={buildDoc} />
        </div>
      </div>

      {allWithheld ? (
        <EmptyState title="Withheld" detail="Your access does not show you these figures in either year." />
      ) : empty ? (
        <EmptyState title="No sales in these two years" detail="An empty result for the data you are permitted to see — not a refusal." />
      ) : (
        /* TWO PANELS, NOT A STACK. The chart used to run the full width of
           the card and the twelve months hung below it, so the page was a
           tall scroll of one picture and one table and nothing was ever seen
           together. Side by side, the shape and the figures answer each
           other, and the whole tab fits a screen. */
        <div className="scroll-slim min-h-0 flex-1 overflow-y-auto px-5 py-3">
          <div className="grid min-h-0 items-start gap-4 @5xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col gap-4">
              {/* What the shape adds up to — the same months, summed, so the
                  reader is not left to eyeball a total off a line. */}
              <div className="grid grid-cols-1 gap-3 @md:grid-cols-3">
                <TrendStat measureKey="totalThis" label={`${spanLabel} ${thisYear}`} value={marked(compact(curTotal, currency), curPartial)} full={markedFull(money(curTotal, currency), curPartial)} tone="accent" />
                <TrendStat measureKey="totalPrev" label={`${spanLabel} ${lastYear}`} value={marked(compact(prevTotal, currency), prevPartial)} full={markedFull(money(prevTotal, currency), prevPartial)} />
                <TrendStat
                  measureKey="changePct"
                  label="Change"
                  value={prevTotal === 0 ? DASH : marked(`${curTotal >= prevTotal ? '+' : '−'}${(Math.abs((curTotal - prevTotal) / prevTotal) * 100).toFixed(1)}%`, curPartial || prevPartial)}
                  full={markedFull(`${curTotal >= prevTotal ? '+' : '−'}${money(Math.abs(curTotal - prevTotal), currency)} against the same months`, curPartial || prevPartial)}
                  tone={curTotal >= prevTotal ? 'good' : 'bad'}
                />
              </div>

              <section className="rounded-md border border-(--color-line) bg-(--color-surface) p-4 shadow-(--shadow-card)">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                  <h3 className="text-(length:--text-base) font-semibold text-(--color-ink)">
                    {m === 'gp' ? 'Gross profit' : 'Sales'} by month
                  </h3>
                  {/* The legend belongs beside the title, not stranded under
                      the plot where it read as a caption for the table. */}
                  <div className="flex items-center gap-4">
                    {[
                      { year: thisYear, colour: 'var(--series-1)' },
                      { year: lastYear, colour: 'var(--series-2)' },
                    ].map((s) => (
                      <span key={s.year} className="inline-flex items-center gap-1.5 text-(length:--text-small) text-(--color-ink-2)">
                        <span className="h-[3px] w-4 rounded-full" style={{ background: s.colour }} />
                        {s.year}
                      </span>
                    ))}
                  </div>
                </div>
                {curPartial || prevPartial ? (
                  <p className="mb-2 text-(length:--text-small) text-(--color-ink-3)">
                    Some months leave out amounts your access does not show you; the lines draw what you may see.
                  </p>
                ) : null}
                <TrendPlot cur={cur} prev={prev} thisYear={thisYear} lastYear={lastYear} hover={hover} setHover={setHover} currency={currency} />
              </section>
            </div>

            <TrendTable
              rows={orderedMonths}
              thisYear={thisYear}
              lastYear={lastYear}
              currency={currency}
              hover={hover}
              setHover={setHover}
              sort={sort}
              toggle={toggle}
              ariaSort={ariaSort}
            />
          </div>
        </div>
      )}
    </div>
  )
}

/* The plot's own frame. Narrower and shorter than the receivables chart it
   was cloned from, because this one now lives in a column beside the months
   rather than across the whole card: at 960 wide the viewBox was scaled to
   about three quarters here and every axis label shrank with it. These
   numbers render near 1:1 in the panel, so the labels keep their size. */
const W = 720
const H = 260
const ML = 62
const MR = 14
const MT = 12
const MB = 34

function TrendPlot({
  cur,
  prev,
  thisYear,
  lastYear,
  hover,
  setHover,
  currency,
}: {
  cur: (number | null)[]
  prev: (number | null)[]
  thisYear: number
  lastYear: number
  hover: number | null
  setHover: (i: number | null) => void
  currency: Currency
}) {
  // The axis type keeps its stated size at every width — see useSvgTextScale.
  const { svgRef, k } = useSvgTextScale(W)
  const plotW = W - ML - MR
  const plotH = H - MT - MB

  const all = [...cur, ...prev].filter((v): v is number => v !== null)
  // Zero always on the axis for money — the receivables chart's rule, kept.
  const yMax = Math.max(1, ...all) * 1.08
  const yMin = Math.min(0, ...all) * 1.08

  const x = (i: number) => ML + (i / 11) * plotW
  const y = (v: number) => MT + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH

  const ticks = 4
  const grid = Array.from({ length: ticks + 1 }, (_, i) => yMin + ((yMax - yMin) * i) / ticks)

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - rect.left) / rect.width) * W
    setHover(Math.max(0, Math.min(11, Math.round(((px - ML) / plotW) * 11))))
  }

  /** A line only through the months that exist — no line to a null. */
  const path = (vals: (number | null)[]) => {
    let d = ''
    vals.forEach((v, i) => {
      if (v === null) return
      d += `${d ? ' L' : 'M'}${x(i)},${y(v)}`
    })
    return d
  }

  const series = [
    { label: String(thisYear), vals: cur, color: 'var(--series-1)' },
    { label: String(lastYear), vals: prev, color: 'var(--series-2)' },
  ]

  return (
    <div className="relative w-full">
      <svg
        ref={svgRef}
        id="sales-trend-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Monthly figures, ${thisYear} against ${lastYear}`}
        className="block h-auto w-full cursor-crosshair"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {grid.map((v, i) => (
          <g key={i}>
            <line x1={ML} x2={W - MR} y1={y(v)} y2={y(v)} stroke="var(--color-line)" strokeWidth={1} />
            <text x={ML - 10} y={y(v) + 3.5} textAnchor="end" fontSize={11 * k} fill="var(--color-ink-3)" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {compact(v)}
            </text>
          </g>
        ))}
        <line x1={ML} x2={W - MR} y1={y(0)} y2={y(0)} stroke="var(--color-line-strong)" strokeWidth={1} />

        {MONTHS.map((mo, i) => (
          <text key={mo} x={x(i)} y={H - MB + 20} textAnchor="middle" fontSize={10.5 * k} fill="var(--color-ink-3)">
            {mo}
          </text>
        ))}

        {hover !== null && (
          <line x1={x(hover)} x2={x(hover)} y1={MT} y2={MT + plotH} stroke="var(--color-navy-400)" strokeWidth={1} />
        )}

        {series.map((s) => (
          <g key={s.label}>
            <path d={path(s.vals)} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            {s.vals.map((v, i) =>
              v !== null && (i === s.vals.length - 1 - [...s.vals].reverse().findIndex((z) => z !== null) || hover === i) ? (
                <circle key={i} cx={x(i)} cy={y(v)} r={hover === i ? 4.5 : 4} fill={s.color} stroke="var(--color-surface)" strokeWidth={hover === i ? 1.5 : 0} />
              ) : null,
            )}
          </g>
        ))}
      </svg>

      {hover !== null && (
        <div
          className="pointer-events-none absolute top-0 min-w-[180px] rounded-md border border-(--color-line-strong) bg-(--color-surface) px-3 py-2 text-(length:--text-small) shadow-lg"
          style={{
            left: `${(x(hover) / W) * 100}%`,
            transform: x(hover) > W / 2 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
          }}
        >
          <div className="mb-1.5 font-semibold text-(--color-ink)">{MONTHS[hover]}</div>
          {series.map((s) => (
            <div key={s.label} className="flex items-center gap-2 py-px">
              <span className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ background: s.color }} />
              <span className="flex-1 text-(--color-ink-2)">{s.label}</span>
              <span className="num font-semibold text-(--color-ink)">
                {s.vals[hover] === null ? DASH : money(s.vals[hover]!, currency)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** A compact figure for the trend's summary strip — smaller than StatTile,
 *  which is sized for a KPI head rather than a supporting row. */
function TrendStat({
  label,
  value,
  full,
  tone = 'none',
  measureKey,
}: {
  label: string
  value: string
  full?: string
  tone?: 'none' | 'accent' | 'good' | 'bad'
  /** Developer mode. All three of these are computed in the BROWSER from the
      monthly rows, and the panel says so — "the server said this" and "your
      browser worked this out" are different degrees of trust. */
  measureKey?: string
}) {
  const colour = {
    none: 'text-(--color-ink)',
    accent: 'text-(--color-ink)',
    good: 'text-(--color-good)',
    bad: 'text-(--color-overdue)',
  }[tone]
  return (
    <div className="relative overflow-hidden rounded-md border border-(--color-line) bg-(--color-surface) px-3 py-2.5 shadow-(--shadow-card)">
      {tone === 'accent' && <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-(--color-navy-700)" />}
      <div className="truncate text-(length:--text-small) text-(--color-ink-3)" title={label}>
        {measureKey ? (
          <Explain surface="trend" measureKey={measureKey}>
            {label}
          </Explain>
        ) : (
          label
        )}
      </div>
      <div className={`num mt-0.5 truncate text-[19px] font-semibold leading-tight ${colour}`} title={full}>
        {value}
      </div>
    </div>
  )
}

/**
 * The twelve months, as a panel beside the plot rather than a slab beneath
 * it. Hovering a row lights the same month on the chart and vice versa —
 * two views of one series should not need the reader to hold a place.
 */
function TrendTable({
  rows,
  thisYear,
  lastYear,
  currency,
  hover,
  setHover,
  sort,
  toggle,
  ariaSort,
}: {
  /* Already ordered by the caller. The sort lives up there because the EXTRACT
     has to ship this same order, and only the caller builds the extract. */
  rows: TrendMonthRow[]
  thisYear: number
  lastYear: number
  currency: Currency
  hover: number | null
  setHover: (i: number | null) => void
  sort: SortState<TrendSortKey>
  toggle: (key: TrendSortKey) => void
  ariaSort: (key: TrendSortKey) => 'ascending' | 'descending' | 'none'
}) {
  return (
    <section className="flex min-h-0 flex-col rounded-md border border-(--color-line) bg-(--color-surface) shadow-(--shadow-card)">
      <h3 className="shrink-0 border-b border-(--color-line) px-4 py-2 text-(length:--text-base) font-semibold text-(--color-ink)">
        Month by month
      </h3>
      <div className="scroll-slim min-h-0 flex-1 overflow-auto">
        <table className="report-table w-full border-collapse text-(length:--text-body)">
          <thead className="sticky top-0 z-10">
            <tr>
              {/* The year headings are the YEAR, not the key -- the panel is
                  asked about `thisYear` and `lastYear` whatever the label
                  reads, which is what keeps the documentation stable across a
                  January.

                  `eyebrow` moved from the label onto the CELL: it only ever
                  carried typography, all of it inherited, so the heading sets
                  exactly as before while the label becomes a sort button. */}
              {([
                { key: 'month', label: 'Month', measureKey: 'month' },
                { key: 'thisYear', label: String(thisYear), measureKey: 'thisYear' },
                { key: 'lastYear', label: String(lastYear), measureKey: 'lastYear' },
                { key: 'change', label: 'Change', measureKey: 'change' },
              ] as const).map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={ariaSort(col.key)}
                  className="eyebrow border-b border-(--color-line-strong) px-3 py-1 text-center"
                >
                  <SortHeader
                    label={col.label}
                    active={sort.key === col.key}
                    desc={sort.desc}
                    onToggle={() => toggle(col.key)}
                    surface="trend"
                    measureKey={col.measureKey}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ i, month, a, b, d, aWithheld, bWithheld }) => {
              const tone = d === null || d === 0 ? 'text-(--color-ink-3)' : d > 0 ? 'text-(--color-good)' : 'text-(--color-overdue)'
              return (
                <tr
                  key={month}
                  /* The hover is the MONTH's index into the plotted series, not
                     this row's position in the table — the two are the same
                     number only while the table is in calendar order. */
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  className={`border-b border-(--color-line) ${hover === i ? 'bg-(--color-surface-2)' : ''}`}
                >
                  <td className="whitespace-nowrap px-3 py-1 text-(--color-ink)">{month}</td>
                  <td className="num whitespace-nowrap px-3 py-1 text-right text-(--color-ink)">{a === null ? (aWithheld ? WITHHELD : DASH) : money(a, currency)}</td>
                  <td className="num whitespace-nowrap px-3 py-1 text-right text-(--color-ink-3)">{b === null ? (bWithheld ? WITHHELD : DASH) : money(b, currency)}</td>
                  <td className={`num whitespace-nowrap px-3 py-1 text-right font-semibold ${tone}`}>
                    {d === null ? (aWithheld || bWithheld ? WITHHELD : DASH) : `${d >= 0 ? '+' : '−'}${money(Math.abs(d), currency)}`}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

/* ===========================================================================
   THE TREE — decomposition by any dimension, by sales or by GP. The same
   reading the receivables tree established: bars compared by magnitude,
   click to descend, the path worn as removable crumbs.
   =========================================================================== */

/**
 * One step of the drill path: a dimension, and the members of it that are open.
 *
 * SEVERAL members, since 23 Sep 2026 — "let me take these three teams together
 * and see who they sell to". One member is a list of one, so a single click and
 * a selection of one produce the identical request rather than two paths that
 * could ever disagree.
 */
interface TreeStep {
  field: SalesDimension
  values: string[]
}

function SalesTree({
  api,
  viewAs,
  period,
  range,
  currency,
  filters,
  companyNames,
}: {
  api: Api
  viewAs: string | null
  period: SalesPeriod
  range: CustomRange | null
  currency: Currency
  filters: SalesFilters
  /** The KPI head's answer, for the currency this tree is in. See dimsOf —
      a deep drill may narrow to one currency where the head still says mixed,
      and the code is then withheld rather than guessed. */
  overview: SalesOverview | null
  /** Code to name, so the extract names companies rather than coding them. */
  companyNames?: Map<string, string>
}) {
  const [steps, setSteps] = useState<TreeStep[]>([])
  const [groupBy, setGroupBy] = useState<SalesDimension>('BusinessUnit')
  const [measure, setMeasure] = useState<'sales' | 'gp'>('sales')
  /* PER BAR. Each node carries its own code, so a bar inside one company
     reads "SAR 1.2B" even where the level above it spans many. The LEVEL
     TOTAL is the one figure that has to fold them: a code only when every bar
     on screen agrees, because the total really is their sum. */
  const [data, setData] = useState<SalesTreeResponse | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [loading, setLoading] = useState(true)

  /**
   * The bars ticked at THIS level but not yet drilled into. Empty is the normal
   * state: clicking a bar still descends immediately, and this only fills when
   * somebody deliberately ticks boxes.
   */
  const [picked, setPicked] = useState<string[]>([])

  /* The path, per dimension. It no longer collides with the standing filter
     bar: `path` is its own parameter set, so a tree step now narrows WITHIN the
     bar's Business Team rather than replacing it — which is what it silently
     did while the two shared `bt`. */
  const path = useMemo(() => {
    const p: Partial<Record<SalesDimension, string[]>> = {}
    for (const s of steps) p[s.field] = s.values
    return p
  }, [steps])

  const load = useCallback(async () => {
    setLoading(true)
    setFailure(null)
    try {
      const { data: r, vis: rVis } = await api.salesTree({
        ...filters,
        path,
        period,
        from: range?.from,
        to: range?.to,
        measure,
        groupBy,
        currency,
      })
      setData(r)
      setV2(rVis !== null)
      // The server may have corrected the request — a GP tree asked for
      // without the permission comes back drawn by sales, and the controls
      // must follow what came back rather than what was asked.
      if (r.measure !== measure) setMeasure(r.measure as 'sales' | 'gp')
      if (r.groupedBy && r.groupedBy !== groupBy) setGroupBy(r.groupedBy as SalesDimension)
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
    } finally {
      setLoading(false)
    }
  }, [api, viewAs, period, range, currency, filters, path, measure, groupBy])

  useEffect(() => {
    void load()
  }, [load])

  // A different pair of eyes is a different book — start at the root.
  useEffect(() => {
    setSteps([])
    setGroupBy('BusinessUnit')
    setMeasure('sales')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAs])

  if (failure) return <FailureState failure={failure} onRetry={() => void load()} />

  const nodes = data?.nodes ?? []
  const largest = Math.max(1, ...nodes.map((n) => Math.abs(n.value ?? 0)))
  const levelCode = foldCode(nodes.map((n) => n.currencyCode))
  /* UNDER THE NEW MODEL a bar can be withheld while its neighbours show, and one
     the row's Partial names leaves withheld values out (M8): the level total is
     the sum of what shows, marked when it leaves anything out, and withheld only
     when no bar shows. */
  const shownTotal = v2 ? sumShown(nodes, ['value', (n) => n.value]) : null
  const total = shownTotal ? (shownTotal.value ?? 0) : nodes.reduce((s, n) => s + (n.value ?? 0), 0)
  const blank = v2 ? WITHHELD : DASH
  /** Every picked bar withheld: their sum is withheld, not 0 (16d's review). */
  const pickedWithheld = v2 && picked.length > 0
    && nodes.filter((n) => picked.includes(n.nodeKey ?? '')).every((n) => n.value === null)
  const totalText = shownTotal && shownTotal.value === null
    ? WITHHELD
    : `${money(total, currency, levelCode)}${shownTotal?.partial ? '*' : ''}`
  const gpOffered = (data?.availableMeasures ?? []).includes('gp')
  const spent = new Set(steps.map((s) => s.field))
  const offerable = DIMENSIONS.filter((d) => !spent.has(d.value))

  /* THE EXTRACT — this level's bars as a table: share of the level total,
     the analysed figure, and the tooltip figures, path stated in full. */
  const buildDoc = (): ExportDoc | null => {
    if (loading || nodes.length === 0) return null
    const measureLabel = measure === 'gp' ? 'Gross profit' : 'Sales'
    const groupLabel = DIMENSIONS.find((d) => d.value === groupBy)?.label ?? groupBy
    /* A step may hold SEVERAL members, and the file has to say so: a reader
       quoting "Business Team: Gulf Retail" from a document that actually covers
       three teams is the precise failure the export rules exist to prevent —
       the same argument the customer-contains line makes above. Short lists are
       named in full, because "3 teams" is not something a reader can check. */
    const pathLine =
      steps.length > 0
        ? [
            `Path: ${steps
              .map((s) => {
                const label = DIMENSIONS.find((d) => d.value === s.field)?.label ?? s.field
                return s.values.length === 1
                  ? `${label}: ${s.values[0]}`
                  : `${label} (${s.values.length}): ${s.values.join(', ')}`
              })
              .join('  →  ')}`,
          ]
        : []
    return {
      report: 'Sales',
      page: 'Tree View',
      title: `Decomposition, ${periodPhrase(period, range).toLowerCase()}`,
      subtitle: `${measureLabel} broken down by ${groupLabel.toLowerCase()} — ${nodes.length} bar(s), level total ${totalText}.`,
      context: salesExportContext(period, range, currency, filters, [
        `Analysing: ${measureLabel} · broken down by ${groupLabel}`,
        ...pathLine,
      ], companyNames),
      privileged: viewAs,
      currency,
      tables: [
        {
          columns: [
            { header: groupLabel },
            { header: 'Share', kind: 'pct' },
            { header: measureLabel, kind: 'money' },
            { header: 'Sales', kind: 'money' },
            { header: 'GP', kind: 'money' },
            { header: 'GP %', kind: 'pct' },
            { header: 'Lines', kind: 'int' },
          ],
          rows: nodes.map<ExportRow>((n) => ({
            cells: [
              n.nodeKey ?? '(blank)',
              total !== 0 && (n.value !== null || !v2) ? (n.value ?? 0) / total : null,
              n.value === null && v2 ? WITHHELD : n.value,
              n.sales === null && v2 ? WITHHELD : n.sales,
              n.gp === null && v2 ? WITHHELD : n.gp,
              n.gpPct === null && v2 && (n.sales === null || n.gp === null) ? WITHHELD : n.gpPct,
              n.lines,
            ],
          })),
          note: 'Top 20 bars of the level, as drawn on screen. Share is of the level total above.'
            + (v2 && (shownTotal?.partial || nodes.some((n) => partialOf(n).size > 0)) ? ` ${PARTIAL_NOTE}` : ''),
        },
      ],
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-(--color-line) px-5 py-4">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div>
            {/* THE BARS THEMSELVES CANNOT CARRY THE CLICK -- a tree row IS a
                button, it drills, and a button inside a button is invalid HTML.
                So the explanation lives on the control that decides what the
                bars measure, which is also where a reviewer would look: the
                quiet fallback from gross profit to sales when the cost
                permission is missing is documented there. */}
            <Explain surface="tree" measureKey="value" className="eyebrow mb-1.5 block">
              Analyse
            </Explain>
            <Segmented
              label="Measure"
              value={measure}
              onChange={setMeasure}
              options={
                gpOffered
                  ? [
                      { value: 'sales', label: 'Sales' },
                      { value: 'gp', label: 'Gross profit' },
                    ]
                  : [{ value: 'sales', label: 'Sales' }]
              }
            />
          </div>
          <div>
            <Explain surface="tree" measureKey="member" className="eyebrow mb-1.5 block">
              Break down by
            </Explain>
            {/* Only unspent dimensions are offered: a path step by Business Unit
                makes "by Business Unit" a one-bar level that answers nothing.
                Descending auto-advances the grouping (see the bar click), so the
                current value is always in this list. */}
            <Segmented label="Break down by" value={groupBy} onChange={setGroupBy} options={offerable} />
          </div>
        </div>
        <div className="pb-0.5">
          <ExportButtons build={buildDoc} />
        </div>
      </div>

      {steps.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-2.5">
          <span className="eyebrow">Path</span>
          {/* Index in the key: once all six dimensions are spent two steps can
              carry the same (field, value), and duplicate sibling keys corrupt
              reconciliation. The stack is append/truncate-only, so i is stable. */}
          {steps.map((s, i) => {
            const label = DIMENSIONS.find((d) => d.value === s.field)?.label ?? s.field
            /* NAME THEM WHILE THEY FIT. "Business Team: 3 selected" is a crumb
               a reader cannot check without opening something, and the whole
               point of the path strip is that the question is legible at a
               glance. Two names are shorter than the sentence describing them;
               beyond three the crumb would wrap and the count wins, with the
               full list on the tooltip rather than nowhere. */
            const shown =
              s.values.length <= 3
                ? s.values.join(', ')
                : `${s.values.length} selected`
            return (
              <button
                key={`${i}:${s.field}:${s.values.join('\u001F')}`}
                type="button"
                title={`Remove this step and everything after it${s.values.length > 3 ? ` — ${s.values.join(', ')}` : ''}`}
                onClick={() => setSteps(steps.slice(0, i))}
                className="inline-flex max-w-[28rem] items-center gap-1.5 rounded-full border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-1 text-(length:--text-small) text-(--color-ink-2) hover:border-(--color-overdue) hover:text-(--color-overdue)"
              >
                <span className="font-medium">{label}:</span>
                <span className="truncate">{shown}</span>
                <span aria-hidden>×</span>
              </button>
            )
          })}
        </div>
      )}

      {loading && <Loading label="Decomposing…" />}

      {!loading && nodes.length === 0 ? (
        <EmptyState title="Nothing under this path" detail="An empty result for the data you are permitted to see — not a refusal." />
      ) : (
        <div className="scroll-slim min-h-0 flex-1 overflow-y-auto p-5">
          <p className="mb-3 text-(length:--text-small) text-(--color-ink-3)">
            {nodes.length} bar(s) · level total{' '}
            <span className="num font-semibold text-(--color-ink)">{totalText}</span>
          </p>
          {/* A TREE ROW IS ITSELF A BUTTON, so the five figures it carries have
              nowhere to hang an explanation -- and a documented figure nobody
              can reach is exactly the failure the manifest exists to prevent.
              This strip is the handle. Each entry renders as a bare span for
              everyone else, so the whole row collapses to nothing unless the
              reader has developer mode on. */}
          <TreeLegend />
          {/* THE SELECTION BAR. It appears only once something is ticked, and
              it states the figure as well as the count: "3 teams" is a number
              of rows, and what the reader is about to drill into is a number of
              riyals. Seeing the subtotal before descending is what makes the
              selection checkable — it is the one moment they can notice they
              ticked the wrong team. */}
          {picked.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-3 rounded-md border border-(--color-navy-400) bg-(--color-navy-50) px-3 py-2">
              <span className="text-(length:--text-small) text-(--color-ink-2)">
                <span className="font-semibold text-(--color-ink)">{picked.length}</span>{' '}
                {(DIMENSIONS.find((d) => d.value === groupBy)?.label ?? groupBy).toLowerCase()}
                {picked.length === 1 ? '' : 's'} selected ·{' '}
                <span className="num font-semibold text-(--color-ink)">
                  {pickedWithheld ? WITHHELD : money(
                    nodes.filter((n) => picked.includes(n.nodeKey ?? '')).reduce((s, n) => s + (n.value ?? 0), 0),
                    currency,
                    foldCode(nodes.filter((n) => picked.includes(n.nodeKey ?? '')).map((n) => n.currencyCode)),
                  )}
                  {!pickedWithheld && v2 && nodes.some((n) => picked.includes(n.nodeKey ?? '') && (n.value === null || partialOf(n).has('value'))) ? '*' : null}
                </span>
              </span>
              <span className="flex-1" />
              <Button
                size="sm"
                onClick={() => {
                  /* The same descent a single click makes, with a set instead
                     of a member — which is why there is no second code path
                     and no second thing that can go wrong. */
                  const nextSteps = [...steps, { field: groupBy, values: [...picked] }]
                  const spentNext = new Set(nextSteps.map((s) => s.field))
                  const next = DIMENSIONS.find((d) => !spentNext.has(d.value))
                  setSteps(nextSteps)
                  setPicked([])
                  if (next) setGroupBy(next.value)
                }}
              >
                Drill into {picked.length} together →
              </Button>
              <button
                type="button"
                onClick={() => setPicked([])}
                className="text-(length:--text-small) text-(--color-ink-3) underline-offset-2 hover:text-(--color-ink) hover:underline"
              >
                Clear
              </button>
            </div>
          )}
          <ol className="space-y-0.5">
            {nodes.map((n) => {
              const value = n.value ?? 0
              const share = total !== 0 ? (value / total) * 100 : 0
              // `?.trim()` to match the API's IsNullOrWhiteSpace normalisation,
              // and `disabled` rather than a bare early-return in the handler:
              // every sibling drill removes the affordance when it cannot
              // advance, and a fully-styled focusable button that silently eats
              // the click is the one thing worse than a disabled one.
              // ...and only while a dimension is LEFT to break down by. At the
              // last one the auto-advance below finds no `next`, so the bar
              // would re-drill into itself with the path growing on each click.
              const canDrill =
                !!n.nodeKey?.trim() && DIMENSIONS.some((d) => d.value !== groupBy && !spent.has(d.value))
              const key = n.nodeKey ?? ''
              const ticked = picked.includes(key)
              return (
                <li key={n.nodeKey ?? '(blank)'} className="flex items-center gap-1">
                  {/* THE TICK BOX IS A SIBLING OF THE ROW BUTTON, never inside
                      it: a button cannot contain a button, and the row IS the
                      button.

                      ALWAYS VISIBLE, not revealed on hover. It was a hover
                      reveal first, on the usual argument that a column of empty
                      boxes is noise — but a control nobody can see is a control
                      nobody uses, and this one answers a question a manager
                      asked for out loud. A reader should be able to tell that
                      several bars can be taken together by LOOKING at the
                      chart, not by moving the mouse across it and noticing
                      something appear.

                      Clicking the row still descends into that one member,
                      exactly as before. Nobody's habit breaks; the boxes are
                      the second way, for the second question.

                      The slot is kept (invisible, not removed) on a bar that
                      cannot be drilled, so the labels stay in one column
                      whether or not a row is selectable. */}
                  <span className={`grid w-6 shrink-0 place-items-center ${canDrill ? '' : 'invisible'}`}>
                    <input
                      type="checkbox"
                      checked={ticked}
                      disabled={!canDrill}
                      aria-label={`Select ${n.nodeKey ?? '(blank)'}`}
                      onChange={() =>
                        setPicked((p) => (p.includes(key) ? p.filter((v) => v !== key) : [...p, key]))
                      }
                      className="h-3.5 w-3.5 cursor-pointer accent-(--color-navy-700)"
                    />
                  </span>
                  <button
                    type="button"
                    disabled={!canDrill}
                    onClick={() => {
                      if (!canDrill) return
                      // Descend, and advance the grouping to the next dimension
                      // the path has not spent — staying on the one just used
                      // would draw a single bar equal to the step.
                      const nextSteps = [...steps, { field: groupBy, values: [n.nodeKey as string] }]
                      const spentNext = new Set(nextSteps.map((s) => s.field))
                      const next = DIMENSIONS.find((d) => !spentNext.has(d.value))
                      setSteps(nextSteps)
                      setPicked([])
                      if (next) setGroupBy(next.value)
                    }}
                    /* Under a 768px content box the gaps tighten and the value
                       column sizes to its figure instead of a fixed 160px —
                       otherwise the name, which is the whole label, got
                       nothing on a phone and the figure ran off the edge.
                       The receivables tree carries the same three classes. */
                    className="group relative flex w-full items-center gap-4 rounded px-3 py-2 text-left @max-3xl:gap-2 @max-3xl:px-2 transition-colors hover:bg-(--color-navy-50) disabled:cursor-default disabled:hover:bg-transparent"
                    title={`${n.nodeKey ?? '(blank)'} — sales ${n.sales === null ? blank : money(n.sales, currency, n.currencyCode)}${n.gp === null ? '' : `, GP ${money(n.gp, currency, n.currencyCode)}`}${n.gpPct === null ? '' : `, ${pct(n.gpPct)}`}`}
                  >
                    {/* The bar sits BEHIND the label rather than beside it —
                        the receivables tree's rule, kept for the same two
                        reasons: a short code leaves no dead gap in front of
                        its bar, and a long customer name cannot squeeze the
                        graphic the visual exists to show. Negative GP washes
                        red, exactly like a negative amount over there. */}
                    <span
                      aria-hidden
                      style={{ width: `${(Math.abs(value) / largest) * 100}%` }}
                      className={`absolute inset-y-1 left-0 rounded-sm transition-all duration-300 ${
                        value < 0 ? 'bg-(--color-overdue)/12' : 'bg-(--color-navy-500)/14'
                      }`}
                    />
                    <span className="relative min-w-0 flex-1 truncate font-medium text-(--color-ink)">
                      {n.nodeKey || <span className="italic text-(--color-ink-4)">(blank)</span>}
                    </span>
                    <span className="relative shrink-0 text-(length:--text-small) text-(--color-ink-4)">
                      {n.lines.toLocaleString('en-US')} line{n.lines === 1 ? '' : 's'}
                    </span>
                    <span className="relative w-16 shrink-0 text-right text-(length:--text-small) text-(--color-ink-3) @max-3xl:w-11">
                      {v2 && n.value === null ? '' : `${share.toFixed(1)}%`}
                    </span>
                    <span
                      className={`num relative w-40 shrink-0 text-right font-semibold @max-3xl:w-auto ${
                        value < 0 ? 'text-(--color-overdue)' : 'text-(--color-ink)'
                      }`}
                    >
                      {n.value === null ? blank : `${money(n.value, currency, n.currencyCode)}${partialOf(n).has('value') ? '*' : ''}`}
                    </span>
                  </button>
                </li>
              )
            })}
          </ol>
        </div>
      )}
    </div>
  )
}




